/**
 * ExecutiveDashboardShell — F5: Executive Persona Dashboard Layout
 *
 * Renders the Executive persona dashboard. Each widget slot is driven by the
 * ordered visible widget list from DashboardWidgetPreferencesContext so that
 * personalisation (visibility and ordering) applies automatically.
 *
 * Widget implementations are added progressively in tasks F8–F20. For widgets
 * not yet built, a clearly labelled "Coming Soon" placeholder card is rendered
 * so the shell is fully functional as a layout container from day one.
 *
 * Existing dashboard content (KPIs, At-Risk projects, Profitability, Revenue vs
 * Cost charts, Project Health table, Aged WIP alert, Resourcing alerts) has been
 * migrated here from DashboardPageInner as the initial Executive widget set.
 *
 * PRD Layout (§3):
 *   Row 1  — Portfolio Pulse (4 KPI tiles, full width)
 *   Row 2  — Projects Requiring Attention | Upcoming Key Dates
 *   Row 3  — Portfolio Profitability | Revenue vs Cost Burn
 *   Row 4  — Billing & Cash Flow Health | Client Concentration Risk
 *   Row 5  — Utilisation Snapshot | Headcount & Delivery Capacity
 *   Row 6  — Quick Links (full width)
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,

  FolderKanban,
  Users,
  UserCheck,
  UserMinus,
  AlertTriangle,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Minus,
  Target,
  CheckCircle2,
  CalendarClock,
  Construction,
  Activity,
  Plug,
  Gauge,
  Banknote,
  FileText,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  CreditCard,
  PieChart as PieChartIcon,
  Building2,
  Calendar,
  Flag,
  Clock,
  AlertCircle,
  BarChart2,
  BookOpen,
  Layers,
  ClipboardList,
  WalletCards,
  Receipt,
  ListChecks,
  BadgeAlert,
  CircleDollarSign,
  Filter,
  ChevronDown,
  ChevronUp,
  Settings,
  Lock,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

import { SensitiveData } from '@/components/sensitive-data';
import { AgedWipStrip } from '@/components/billing/AgedWipStrip';
import type { AgeBand } from '@/components/billing/AgedWipStrip';
import { useDashboardWipView } from '@/hooks/use-dashboard-wip-view';
import { KpiCard, CompactKpiCard } from '@/components/kpi-card';
import { useDashboardData } from '@/contexts/DashboardDataContext';
import { PMCrossPersonaDataProvider, PMCrossPersonaWidget } from '@/components/dashboard/PMCrossPersonaWidgets';
import {
  WIDGET_REGISTRY,
  useDashboardWidgetPreferences,
  DashboardWidgetPreferencesContext,
  type DashboardWidgetPreferencesContextValue,
} from '@/contexts/DashboardWidgetPreferencesContext';
import {
  isPulseTileHiddenForRelease,
  isWidgetHiddenForReleaseAnyPersona,
} from '@/config/dashboardReleaseConfig';
import {
  fetchPortfolioSummary,
  fetchClients,
  fetchEmployees,
  fetchProjectMonthlySummary,
  fetchPortfolioMonthlySummary,
  fetchDashboardKpiSnapshot,
  fetchUtilisationByDateRange,
  getDataMode,
} from '@/lib/data-service';
import type { MonthlyFinancialSummary, DashboardKpiSnapshot } from '@/lib/data-service';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';
import {
  cn,
  formatCurrency,
  formatPercent,
  healthToVariant,
  formatStatus,
  getVarianceColor,
} from '@/lib/utils';
import { getProjectStatusColor } from '@/lib/status-colors';
import type { PortfolioSummary, Client, Employee } from '@/types';
import { PROJECT_STATUS_LABELS } from '@/types';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import {

  mockProjectBookingHealth,
  type ProjectBookingHealth,
} from '@/lib/mock-scheduling-data';

import { useDashboardIntegrationStatus } from '@/hooks/use-dashboard-integration-status';
import { useWipThresholds } from '@/hooks/use-wip-thresholds';
import { useMonthEndProximity } from '@/hooks/use-month-end-proximity';
import { usePagedFinancialSummary } from '@/hooks/use-paged-financial-summary';
import { calculateGP } from '@/lib/gp-calculation';
import {
  getFinanceSettings,
  DEFAULT_PORTFOLIO_TARGET_MARGIN_PERCENT,
  DEFAULT_TARGET_UTILISATION_PERCENT,
  DEFAULT_CONCENTRATION_THRESHOLD_PERCENT,
} from '@/lib/finance-settings-storage';
import { ProjectManagerDashboardShell } from './ProjectManagerDashboardShell';
import { useEacSettings } from '@/contexts/EacSettingsContext';

// Under-billing below this amount is noise and is not listed (F35).
const UNDER_BILLING_THRESHOLD = 500;
// Demo/tour derives at-risk rows client-side and unbounded, so the widget caps them here;
// live mode gets exactly `atRiskRowLimit` rows from the API and needs no client cap.
const AT_RISK_DEMO_VISIBLE_ROWS = 5;

// ── Portfolio Forecast widget — mock data ─────────────────────────────────────
const mockPortfolioForecast = {
  portfolioEacCost: 2_840_000,
  portfolioForecastRevenue: 3_450_000,
  portfolioForecastMargin: 17.7,
  projectsOverBudget: 3,
  htcCoverage: null as number | null, // null = no data submitted yet
};

const mockTop5EacVariance: {
  projectId: number;
  name: string;
  pm: string;
  eacCost: number;
  budget: number;
  variance: number;
  health: 'red' | 'amber' | 'green';
}[] = [
  { projectId: 1, name: 'Tension Table Rental',        pm: 'A Nother', eacCost: 142_500, budget: 120_000, variance: 22_500,  health: 'red'   },
  { projectId: 2, name: 'Pipeline Inspection Phase 2', pm: 'J Smith',  eacCost:  98_000, budget:  90_000, variance:  8_000,  health: 'amber' },
  { projectId: 3, name: 'Subsea Survey Q3',            pm: 'R Patel',  eacCost: 210_000, budget: 205_000, variance:  5_000,  health: 'amber' },
  { projectId: 4, name: 'Riser Analysis 2026',         pm: 'A Nother', eacCost:  67_000, budget:  75_000, variance: -8_000,  health: 'green' },
  { projectId: 5, name: 'Offshore Inspection Prep',    pm: 'L Jones',  eacCost: 310_000, budget: 300_000, variance: 10_000,  health: 'amber' },
];

// ── Coming-soon placeholder for widgets not yet built ─────────────────────────

interface ComingSoonCardProps {
  widgetId: string;
  title: string;
  description: string;
  className?: string;
}

function ComingSoonCard({ widgetId, title, description, className }: ComingSoonCardProps) {
  return (
    <Card className={`border-dashed border-2 border-muted ${className ?? ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Construction className="h-4 w-4 shrink-0" />
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        </div>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground italic">
          Widget implementation coming soon (task {widgetId}).
        </p>
      </CardContent>
    </Card>
  );
}

// ── PM Widget Bridge ──────────────────────────────────────────────────────────
// Renders a single PM widget inside the Executive dashboard.
// The ProjectManagerDashboardShell reads `orderedVisibleWidgets` from the
// DashboardWidgetPreferencesContext.  Since the Exec shell has its own context
// instance (scoped to 'executive'), we override the context for the PM shell
// with a read-only value that exposes only the one widget being bridged.
// This lets the PM shell render exactly one widget without any widget-list
// mutations bleeding back into the Exec preferences.

interface PmWidgetBridgeProps {
  widgetId: string;
  onNavigate: (page: string) => void;
}

// Stable no-op for read-only bridge context mutations
const _pmBridgeNoop = () => {};

function PmWidgetBridge({ widgetId, onNavigate }: PmWidgetBridgeProps) {
  // Build a minimal read-only context value that presents just this one widget.
  const bridgeCtx = React.useMemo<DashboardWidgetPreferencesContextValue>(() => ({
    orderedVisibleWidgets: [widgetId],
    allWidgetIds: [widgetId],
    isWidgetVisible: (id: string) => id === widgetId,
    toggleWidgetVisibility: _pmBridgeNoop,
    moveWidgetUp: _pmBridgeNoop,
    moveWidgetDown: _pmBridgeNoop,
    reorderWidgets: _pmBridgeNoop,
    addCrossPersonaWidget: _pmBridgeNoop,
    removeCrossPersonaWidget: _pmBridgeNoop,
    resetToDefault: _pmBridgeNoop,
    hasCustomisations: false,
    isLoadingPreferences: false,
    isSaving: false,
  }), [widgetId]);

  return (
    <DashboardWidgetPreferencesContext.Provider value={bridgeCtx}>
      <ProjectManagerDashboardShell
        onNavigate={onNavigate}
        showFinanceWidgetScopeIndicator={false}
      />
    </DashboardWidgetPreferencesContext.Provider>
  );
}

// ── Executive shell ───────────────────────────────────────────────────────────

interface ExecutiveDashboardShellProps {
  onNavigate?: (page: string) => void;
}

// ─── Design System Colour Tokens ─────────────────────────────────────────────
// All chart/SVG colours must reference these constants so they stay in sync
// with the Evo Design System theme (light + dark mode aware).
const C_SUCCESS  = 'var(--success-500)';  // green  — healthy, on track
const C_WARNING  = 'var(--warning-500)';  // amber  — at risk, caution
const C_DANGER   = 'var(--destructive)';  // red    — critical, overdue

// ─── Revenue recognition methods, in the order the method table lists them ────
type RecognitionMethod = 'percentage-of-completion' | 'milestone' | 'time-based' | 'completed-contract';

const METHOD_LABELS: Record<RecognitionMethod, string> = {
  'percentage-of-completion': '% of Completion',
  'milestone':                'Milestone',
  'time-based':               'Time-Based',
  'completed-contract':       'Completed Contract',
};

const RECOGNITION_METHODS: RecognitionMethod[] = [
  'percentage-of-completion', 'milestone', 'time-based', 'completed-contract',
];

export function ExecutiveDashboardShell({ onNavigate }: ExecutiveDashboardShellProps) {
  const { statusLabels, getStatusLabel } = useProjectStatuses();

  const navigateHook = useNavigate();
  const handleNavigate = onNavigate || ((page: string) => navigateHook(`/${page}`));
  const { currentCompany } = useCompany();
  const { handleApiError } = useErrorHandler({ useToast: true });

  // Shared data from DashboardDataContext (F1)
  const {
    activeProjects, wipReports, utilisationData, utilisationSummary,
    execKpis, finKpis, atRiskSummary,
    revenueRecognitionSummary,
    clientConcentration,
    profitability,
    keyDates,
    revenueCostBurnByProject,
    billingPipeline,
    legacyRevenueRecognitions,
    kpisLoading, finKpisLoading, atRiskLoading, profitabilityLoading, revenueRecLoading,
    keyDatesLoading, clientConcLoading, rcbByProjectLoading,
    projectsDataLoading, projectsLoading, wipLoading, utilisationLoading,
  } = useDashboardData();

  // Data mode — declared early so it's available for revenue recognition bridging
  const dataMode = getDataMode();
  const isLiveMode = dataMode === 'live';

  // Per-project recognition rows exist in demo/tour only. Live mode reads the portfolio
  // figures the widgets render from `revenueRecognitionSummary`, aggregated server-side,
  // rather than one row per active project.
  const revenueRecognitions = legacyRevenueRecognitions;

  // Integration statuses — drives mode-aware labels and connect prompts (F6/F12)
  const { plannerStatus, timesheetStatus, financeStatus } = useDashboardIntegrationStatus();
  const { thresholds: wipThresholds } = useWipThresholds();
  const monthEnd = useMonthEndProximity();

  // Every WIP figure the widgets below render: full-set aggregates, one
  // server-paged page of the aged-WIP table and the bounded list rows in live
  // mode; the same shape derived from the mock rows in demo/tour.
  const wipView = useDashboardWipView({
    underBillingThreshold: UNDER_BILLING_THRESHOLD,
    agedWipDaysThreshold: wipThresholds.agedWipDaysThreshold,
  });
  // Scheduler/planner connected — used by Utilisation Snapshot booking health column (F15)
  const schedulerConnected = plannerStatus?.status === 'connected';

  // Widget preference order from DashboardWidgetPreferencesContext (F3/F4)
  const { orderedVisibleWidgets } = useDashboardWidgetPreferences();

  // EAC settings — used by Portfolio Forecast widget
  const { eacMethod: pfEacMethod, showHoursToComplete: pfShowHtc } = useEacSettings();

  // Supplementary local data not yet in the shared context
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [supplementLoading, setSupplementLoading] = useState(true);
  // F11 — Revenue vs Cost Burn toggle: 'month' | 'project'
  const [rcbView, setRcbView] = useState<'month' | 'project'>('month');
  const [wipActiveBand, setWipActiveBand] = useState<AgeBand | null>(null);

  const handleApiErrorRef = useRef(handleApiError);
  handleApiErrorRef.current = handleApiError;

  useEffect(() => {
    let ignore = false;

    async function loadSupplementary() {
      setSupplementLoading(true);
      const results = await Promise.allSettled([
        fetchPortfolioSummary('dashboard'),
        fetchClients('dashboard'),
        fetchEmployees('dashboard'),
      ]);

      if (ignore) return;

      const [portfolioResult, clientsResult, employeesResult] = results;

      if (portfolioResult.status === 'fulfilled') {
        setPortfolio(portfolioResult.value);
      } else {
        handleApiErrorRef.current(portfolioResult.reason, '/api/portfolio', 'GET');
      }
      if (clientsResult.status === 'fulfilled') {
        setClients(clientsResult.value);
      } else {
        handleApiErrorRef.current(clientsResult.reason, '/api/clients', 'GET');
      }
      if (employeesResult.status === 'fulfilled') {
        setEmployees(employeesResult.value);
      } else {
        handleApiErrorRef.current(employeesResult.reason, '/api/employees', 'GET');
      }

      setSupplementLoading(false);
    }

    loadSupplementary();
    return () => { ignore = true; };
  }, [currentCompany?.id]);

  // ── Monthly financial data for trend widgets (live mode only) ────────────────
  // Aggregates per-project monthly summaries into portfolio-level monthly data.
  // Used by: Portfolio Pulse trends, Revenue vs Cost Burn (by month),
  // Portfolio Profitability trend deltas, and margin sparkline.

  interface AggregatedMonth {
    monthLabel: string;
    yearLabel: string;
    periodSales: number;
    periodCost: number;
    grossProfit: number;
    actualHours: number;
    cumulativeSales: number;
    cumulativeCost: number;
  }

  const [monthlyPortfolioData, setMonthlyPortfolioData] = useState<AggregatedMonth[]>([]);
  const [perProjectMonthly, setPerProjectMonthly] = useState<Record<number, MonthlyFinancialSummary[]>>({});

  useEffect(() => {
    if (!isLiveMode || projectsDataLoading) return;

    let cancelled = false;

    const loadMonthlyData = async () => {
      // Single aggregated call for portfolio-level charts
      const data = await fetchPortfolioMonthlySummary();
      if (cancelled) return;
      setMonthlyPortfolioData(data.map(m => ({
        monthLabel: m.monthLabel,
        yearLabel: m.yearLabel,
        periodSales: m.periodSales,
        periodCost: m.periodCost,
        grossProfit: m.grossProfit,
        actualHours: m.actualHours,
        cumulativeSales: m.cumulativeSales,
        cumulativeCost: m.cumulativeCost,
      })));

      // Use top/bottom projects from portfolio-profitability endpoint (already fetched in context)
      const projectsToFetch: { id: number; code: string }[] = [];
      if (profitability?.topProjects) {
        for (const p of profitability.topProjects) {
          if (p.id && p.name) projectsToFetch.push({ id: p.id, code: p.name });
        }
      }
      if (profitability?.bottomProjects) {
        for (const p of profitability.bottomProjects) {
          if (p.id && p.name) projectsToFetch.push({ id: p.id, code: p.name });
        }
      }

      if (projectsToFetch.length === 0) return;

      const results = await Promise.allSettled(
        projectsToFetch.map(async (p) => {
          const perProjectData = await fetchProjectMonthlySummary(p.code);
          return { projectId: p.id, data: perProjectData };
        }),
      );

      if (cancelled) return;

      const projMap: Record<number, MonthlyFinancialSummary[]> = {};
      for (const result of results) {
        if (result.status === 'fulfilled' && result.value.data.length > 0) {
          projMap[result.value.projectId] = result.value.data;
        }
      }
      setPerProjectMonthly(projMap);
    };

    void loadMonthlyData();
    return () => { cancelled = true; };
  }, [isLiveMode, projectsDataLoading, profitability, currentCompany?.id]);

  // ── KPI Snapshots for trend deltas (live mode) ────────────────────────────
  const [priorKpiSnapshot, setPriorKpiSnapshot] = useState<DashboardKpiSnapshot | null>(null);

  useEffect(() => {
    // The endpoint requires the X-Company-Id header, which authFetch only sends
    // once company context has resolved.
    if (!isLiveMode || !currentCompany?.id) return;
    let cancelled = false;
    const today = new Date().toISOString().slice(0, 10);
    void fetchDashboardKpiSnapshot('executive', 'monthly', today).then(snap => {
      if (!cancelled) setPriorKpiSnapshot(snap);
    });
    return () => { cancelled = true; };
  }, [isLiveMode, currentCompany?.id]);

  // ── Prior-month utilisation from date-range endpoint (live mode) ──────────
  const [priorMonthUtilData, setPriorMonthUtilData] = useState<{ avgPct: number } | null>(null);

  useEffect(() => {
    if (!isLiveMode) return;
    let cancelled = false;
    const now = new Date();
    const priorStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const priorEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const fmt = (d: Date) => d.toISOString().slice(0, 10);
    void fetchUtilisationByDateRange(fmt(priorStart), fmt(priorEnd)).then(rows => {
      if (cancelled || rows.length === 0) return;
      const avg = rows.reduce((s, r) => s + r.utilisationPercent, 0) / rows.length;
      setPriorMonthUtilData({ avgPct: Math.round(avg * 10) / 10 });
    });
    return () => { cancelled = true; };
  }, [isLiveMode, currentCompany?.id]);

  // Per-dataset loading checks used by the widget-level skeleton gate below.
  // `supplementLoading` covers portfolio/clients/employees fetched locally.

  // ── Derived metrics ──────────────────────────────────────────────────────────
  // In live mode, use pre-computed data from dedicated endpoints.
  // In demo/tour, fall back to client-side derivation from activeProjects.

  const atRiskProjects = isLiveMode
    ? [] // at-risk data comes from atRiskSummary in live mode
    : activeProjects.filter(p => p.health === 'amber' || p.health === 'red');
  const greenProjects = isLiveMode
    ? (execKpis ? execKpis.activeProjects - execKpis.projectsAtRisk : 0)
    : activeProjects.filter(p => p.health === 'green').length;
  const amberProjects = isLiveMode
    ? (atRiskSummary?.counts.amberCount ?? 0)
    : activeProjects.filter(p => p.health === 'amber').length;
  const redProjects = isLiveMode
    ? (atRiskSummary?.counts.redCount ?? 0)
    : activeProjects.filter(p => p.health === 'red').length;

  const gpMethod = getFinanceSettings().gpCalculationMethod;

  // In live mode, use profitability endpoint data for margin/profit
  const totalRevenue = isLiveMode
    ? (profitability?.totalRevenue ?? 0)
    : activeProjects.reduce((sum, p) => sum + (p.actualSales || p.actualRevenue || 0), 0);
  const totalDirectCosts = isLiveMode
    ? (profitability ? profitability.totalRevenue - profitability.totalProfit : 0)
    : activeProjects.reduce((sum, p) => sum + (p.actualCosts ?? 0), 0);
  const totalLabourCosts = isLiveMode
    ? 0
    : activeProjects.reduce((sum, p) => sum + (p.actualLabourCosts ?? 0), 0);
  const totalSubcontractorCosts = isLiveMode
    ? 0
    : activeProjects.reduce((sum, p) => sum + (p.actualSubcontractorCosts ?? 0), 0);
  const portfolioResult = isLiveMode
    ? { grossProfit: profitability?.totalProfit ?? 0, grossMarginPct: profitability?.blendedMarginPercent ?? 0 }
    : calculateGP(
        {
          revenue: totalRevenue,
          totalDirectCosts,
          labourCosts: totalLabourCosts,
          subcontractorCosts: totalSubcontractorCosts,
        },
        gpMethod,
      );
  const totalProfit = portfolioResult.grossProfit;
  const portfolioMargin = isLiveMode
    ? (profitability?.blendedMarginPercent ?? (execKpis?.blendedMarginPercent ?? 0))
    : portfolioResult.grossMarginPct;

  // ── Revenue MTD (F8 — Portfolio Pulse) ──────────────────────────────────────
  // Sum of revenueRecognised across all projects for the current calendar month.
  // Source: revenueRecognitions from DashboardDataContext (PRD §3, Insight 1).
  //
  // ⚠️  ENGINEERING NOTE: This currently uses the cumulative revenueRecognised
  // value from the budget/WIP data — there is no per-month revenue series yet.
  // When the backend provides a time-series revenue endpoint
  // (e.g. GET /api/dashboard/revenue-recognition?period=current-month), replace
  // this derivation with a direct month-scoped API response.
  const revenueMTD = isLiveMode
    ? (execKpis?.revenueRecognisedMtd ?? 0)
    : revenueRecognitions.reduce((sum, rr) => sum + (rr.revenueRecognised ?? 0), 0);

  // Prior-period revenue MTD — in live mode, derived from aggregated monthly data.
  // In demo/tour mode, falls back to ×0.95 approximation.
  const priorMonthRevenueMTD = useMemo(() => {
    if (isLiveMode && monthlyPortfolioData.length >= 2) {
      return monthlyPortfolioData[monthlyPortfolioData.length - 2].periodSales;
    }
    return revenueMTD * 0.95;
  }, [isLiveMode, monthlyPortfolioData, revenueMTD]);
  const revenueMTDTrend =
    priorMonthRevenueMTD > 0
      ? Math.round(((revenueMTD - priorMonthRevenueMTD) / priorMonthRevenueMTD) * 100 * 10) / 10
      : 0;

  // ── Blended margin trend (F8 — Portfolio Pulse) ───────────────────────────
  // In live mode, prior-month margin from aggregated monthly data.
  // In demo/tour, falls back to ×0.97 approximation.
  const priorMonthMargin = useMemo(() => {
    if (isLiveMode && monthlyPortfolioData.length >= 2) {
      const prev = monthlyPortfolioData[monthlyPortfolioData.length - 2];
      return prev.periodSales > 0
        ? ((prev.periodSales - prev.periodCost) / prev.periodSales) * 100
        : 0;
    }
    return portfolioMargin * 0.97;
  }, [isLiveMode, monthlyPortfolioData, portfolioMargin]);
  const marginTrend =
    Math.round((portfolioMargin - priorMonthMargin) * 10) / 10;

  // Margin trend sparkline — in live mode, built from real monthly data.
  // In demo/tour, falls back to hardcoded sample.
  const marginTrendData = useMemo(() => {
    if (isLiveMode && monthlyPortfolioData.length > 0) {
      // Use last 6 months of real data
      const recent = monthlyPortfolioData.slice(-6);
      return recent.map(m => ({
        month: m.monthLabel,
        margin: m.periodSales > 0
          ? Math.round(((m.periodSales - m.periodCost) / m.periodSales) * 100 * 10) / 10
          : 0,
      }));
    }
    return [
      { month: 'Aug', margin: 21.2 },
      { month: 'Sep', margin: 22.1 },
      { month: 'Oct', margin: 23.5 },
      { month: 'Nov', margin: 22.8 },
      { month: 'Dec', margin: 23.9 },
      { month: 'Jan', margin: portfolioMargin },
    ];
  }, [isLiveMode, monthlyPortfolioData, portfolioMargin]);

  const healthDistribution = [
    { name: 'Healthy', value: greenProjects, color: C_SUCCESS },
    { name: 'At Risk', value: amberProjects, color: C_WARNING },
    { name: 'Critical', value: redProjects, color: C_DANGER },
  ];

  const revenueVsCostData = activeProjects
    .sort((a, b) => (b.actualRevenue || 0) - (a.actualRevenue || 0))
    .slice(0, 5)
    .map(p => {
      const pRevenue = p.actualSales || p.actualRevenue || 0;
      const pDirectCosts = p.actualCosts ?? 0;
      const pResult = calculateGP(
        { revenue: pRevenue, totalDirectCosts: pDirectCosts, labourCosts: p.actualLabourCosts ?? 0, subcontractorCosts: p.actualSubcontractorCosts ?? 0 },
        gpMethod,
      );
      return { name: p.name.length > 20 ? p.name.substring(0, 20) + '...' : p.name, revenue: pRevenue, cost: pDirectCosts, profit: pResult.grossProfit };
    });

  // ── F11 — Revenue vs Cost Burn: By Month + By Project data ─────────────────
  // In live mode, uses real monthly data from fetchProjectMonthlySummary.
  // In demo/tour, builds synthetic data distributed evenly across months.
  const portfolioActualRevenue = activeProjects.reduce(
    (s, p) => s + (p.actualSales || p.actualRevenue || 0),
    0,
  );
  const portfolioActualCosts = activeProjects.reduce(
    (s, p) => s + (p.actualCosts ?? 0),
    0,
  );
  // Live mode has no full Project[] to sum, so the portfolio cost budget comes
  // from the executive KPIs endpoint.
  const portfolioBudgetCosts = isLiveMode
    ? (execKpis?.totalCostBudget ?? 0)
    : activeProjects.reduce(
        (s, p) => s + (p.budgetLabour || 0) + (p.budgetExpense || 0),
        0,
      );
  const byMonthData = useMemo(() => {
    if (isLiveMode && monthlyPortfolioData.length > 0) {
      // Real monthly data from aggregated per-project summaries
      const recent = monthlyPortfolioData.slice(-6);
      const avgPlannedPerMonth = recent.length > 0 ? portfolioBudgetCosts / recent.length : 0;
      return recent.map(m => {
        const plannedCost = Math.round(avgPlannedPerMonth);
        const actualRevenue = Math.round(m.periodSales);
        const actualCost = Math.round(m.periodCost);
        const variancePct = plannedCost > 0 ? ((actualCost - plannedCost) / plannedCost) * 100 : 0;
        const actualCostColor =
          variancePct > 15
            ? C_DANGER
            : variancePct > 5
              ? C_WARNING
              : 'var(--primary)';
        return { month: m.monthLabel, plannedCost, actualCost, actualRevenue, actualCostColor, variancePct };
      });
    }
    // Demo/tour fallback: synthetic distribution
    const numMonths = marginTrendData.length || 6;
    return marginTrendData.map((m, i) => {
      const ramp = 0.8 + (i / (numMonths - 1 || 1)) * 0.4;
      const plannedCost = Math.round((portfolioBudgetCosts / numMonths) * ramp);
      const actualRevenue = Math.round((portfolioActualRevenue / numMonths) * ramp);
      const actualCost = Math.round((portfolioActualCosts / numMonths) * ramp);
      const variancePct = plannedCost > 0 ? ((actualCost - plannedCost) / plannedCost) * 100 : 0;
      const actualCostColor =
        variancePct > 15
          ? C_DANGER
          : variancePct > 5
            ? C_WARNING
            : 'var(--primary)';
      return { month: m.month, plannedCost, actualCost, actualRevenue, actualCostColor, variancePct };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveMode, monthlyPortfolioData, marginTrendData, portfolioBudgetCosts, portfolioActualRevenue, portfolioActualCosts]);

  // By Project: top 10 active projects by contract value (budgetRevenue)
  // In live mode, use pre-computed data from the dedicated endpoint.
  const byProjectSorted = isLiveMode
    ? revenueCostBurnByProject
    : [...activeProjects]
        .filter(p => (p.actualSales || p.actualRevenue || 0) > 0)
        .sort((a, b) => (b.budgetRevenue || 0) - (a.budgetRevenue || 0));
  const byProjectTop10 = isLiveMode ? revenueCostBurnByProject : (byProjectSorted as typeof activeProjects).slice(0, 10);
  const byProjectHasMore = byProjectSorted.length > 10;
  const byProjectData = isLiveMode
    ? revenueCostBurnByProject.map(p => {
        const actualCostColor =
          p.variancePct > 15 ? C_DANGER : p.variancePct > 5 ? C_WARNING : 'var(--primary)';
        const shortName = p.name.length > 18 ? p.name.substring(0, 18) + '…' : p.name;
        return { name: shortName, plannedCost: p.plannedCost, actualCost: p.actualCost, actualRevenue: p.actualRevenue, actualCostColor, variancePct: p.variancePct };
      })
    : (byProjectTop10 as typeof activeProjects).map(p => {
        const plannedCost = (p.budgetLabour || 0) + (p.budgetExpense || 0);
        const actualCost = p.actualCosts ?? 0;
        const actualRevenue = p.actualSales || p.actualRevenue || 0;
        const variancePct = plannedCost > 0 ? ((actualCost - plannedCost) / plannedCost) * 100 : 0;
        const actualCostColor = variancePct > 15 ? C_DANGER : variancePct > 5 ? C_WARNING : 'var(--primary)';
        const shortName = p.name.length > 18 ? p.name.substring(0, 18) + '…' : p.name;
        return { name: shortName, plannedCost, actualCost, actualRevenue, actualCostColor, variancePct };
      });

  // Break-even reference value: average of (actualRevenue, actualCost) across all groups
  const breakEvenByMonth =
    byMonthData.length > 0
      ? Math.round(
          byMonthData.reduce((s, d) => s + d.actualRevenue + d.actualCost, 0) /
            (byMonthData.length * 2),
        )
      : 0;
  const breakEvenByProject =
    byProjectData.length > 0
      ? Math.round(
          byProjectData.reduce((s, d) => s + d.actualRevenue + d.actualCost, 0) /
            (byProjectData.length * 2),
        )
      : 0;

  const projectsWithProfit = activeProjects.map(p => {
    const pRevenue = p.actualSales || p.actualRevenue || 0;
    const pDirectCosts = p.actualCosts ?? 0;
    const pResult = calculateGP(
      { revenue: pRevenue, totalDirectCosts: pDirectCosts, labourCosts: p.actualLabourCosts ?? 0, subcontractorCosts: p.actualSubcontractorCosts ?? 0 },
      gpMethod,
    );
    return { ...p, profit: pResult.grossProfit };
  });

  const topProfitable = [...projectsWithProfit].sort((a, b) => b.profit - a.profit).slice(0, 3);
  const bottomProfitable = [...projectsWithProfit].sort((a, b) => a.profit - b.profit).slice(0, 3);

  // ── F10 — Portfolio Profitability: per-project margin % with trend ───────────
  // In live mode, use the pre-computed profitability from the backend endpoint.
  // In demo/tour, derive from activeProjects client-side.
  const projectsWithMargin = isLiveMode && profitability
    ? (profitability.topProjects.concat(profitability.bottomProjects))
        .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i)
        .map(p => ({
          id: p.id,
          name: p.name,
          clientName: p.clientNames ? p.clientNames.split(',')[0].trim() : '',
          statusId: p.statusId,
          marginPct: p.marginPercent,
          marginGbp: p.marginGbp,
          marginTrendDelta: 0,
        }))
        .sort((a, b) => b.marginPct - a.marginPct)
    : activeProjects
        .filter(p => p.actualSales > 0)
        .map(p => {
          const pRevenue = p.actualSales;
          const pCosts = p.actualCosts;
          const marginPct = pRevenue > 0 ? ((pRevenue - pCosts) / pRevenue) * 100 : 0;
          const marginGbp = pRevenue - pCosts;
          let marginTrendDelta = 0;
          if (isLiveMode) {
            const projMonthly = perProjectMonthly[p.id];
            if (projMonthly && projMonthly.length >= 2) {
              const prev = projMonthly[projMonthly.length - 2];
              const priorMarginPct = prev.periodSales > 0
                ? ((prev.periodSales - prev.periodCost) / prev.periodSales) * 100
                : 0;
              marginTrendDelta = Math.round((marginPct - priorMarginPct) * 10) / 10;
            }
          } else {
            const mockDelta = (((p.id * 7) % 11) - 5) * 0.8;
            marginTrendDelta = Math.round(mockDelta * 10) / 10;
          }
          const clientName = p.clientNames ? p.clientNames.split(',')[0].trim() : '';
          return { id: p.id, name: p.name, clientName, statusId: p.statusId, marginPct, marginGbp, marginTrendDelta };
        })
        .sort((a, b) => b.marginPct - a.marginPct);

  // Top 3 / Bottom 3 split (or all if < 6 projects)
  const portfolioProfitabilityAllProjects = projectsWithMargin.length < 6;
  const top3Projects = portfolioProfitabilityAllProjects
    ? projectsWithMargin
    : projectsWithMargin.slice(0, 3);
  const bottom3Projects = portfolioProfitabilityAllProjects
    ? []
    : [...projectsWithMargin].reverse().slice(0, 3).reverse();

  // ── Portfolio Profitability — margin band distribution ──────────────────────
  // In live mode, use backend margin bands. In demo/tour, derive client-side.
  const portfolioTargetMarginPct =
    getFinanceSettings().portfolioTargetMarginPercent ?? DEFAULT_PORTFOLIO_TARGET_MARGIN_PERCENT;

  const marginBands = isLiveMode && profitability
    ? [
        {
          label: 'Loss-making', range: '< 0%', color: 'bg-destructive', textColor: 'text-destructive',
          projects: profitability.lossProjects.map(p => ({ ...p, marginPct: -1, marginGbp: 0, clientName: '', statusId: 2, marginTrendDelta: 0 })),
        },
        {
          label: 'Low', range: '0 – 10%', color: 'bg-warning-500', textColor: 'text-warning-600 dark:text-warning-400',
          projects: Array.from({ length: profitability.marginBands.low }, () => ({ id: 0, name: '', clientName: '', statusId: 2, marginPct: 5, marginGbp: 0, marginTrendDelta: 0 })),
        },
        {
          label: 'Healthy', range: '10 – 25%', color: 'bg-success-500', textColor: 'text-success-700 dark:text-success-400',
          projects: Array.from({ length: profitability.marginBands.healthy }, () => ({ id: 0, name: '', clientName: '', statusId: 2, marginPct: 17.5, marginGbp: 0, marginTrendDelta: 0 })),
        },
        {
          label: 'Strong', range: '≥ 25%', color: 'bg-primary', textColor: 'text-primary',
          projects: Array.from({ length: profitability.marginBands.strong }, () => ({ id: 0, name: '', clientName: '', statusId: 2, marginPct: 30, marginGbp: 0, marginTrendDelta: 0 })),
        },
      ]
    : [
        { label: 'Loss-making', range: '< 0%', color: 'bg-destructive', textColor: 'text-destructive', projects: projectsWithMargin.filter(p => p.marginPct < 0) },
        { label: 'Low', range: '0 – 10%', color: 'bg-warning-500', textColor: 'text-warning-600 dark:text-warning-400', projects: projectsWithMargin.filter(p => p.marginPct >= 0 && p.marginPct < 10) },
        { label: 'Healthy', range: '10 – 25%', color: 'bg-success-500', textColor: 'text-success-700 dark:text-success-400', projects: projectsWithMargin.filter(p => p.marginPct >= 10 && p.marginPct < 25) },
        { label: 'Strong', range: '≥ 25%', color: 'bg-primary', textColor: 'text-primary', projects: projectsWithMargin.filter(p => p.marginPct >= 25) },
      ];

  // ── F12 — Utilisation Snapshot derived data ────────────────────────────────
  // Determine data source mode: planner → "Capacity Outlook", timesheet → "Billable Utilisation"
  // No integration of any kind → show connect prompt.
  const hasAnyUtilisationIntegration = plannerStatus.isConnected || timesheetStatus.isConnected;
  const utilisationMode: 'planner' | 'timesheet' | 'none' =
    timesheetStatus.isConnected ? 'timesheet' : plannerStatus.isConnected ? 'planner' : 'none';
  const utilisationWidgetTitle =
    utilisationMode === 'timesheet' ? 'Billable Utilisation' : 'Capacity Outlook';
  const utilisationWidgetLabel =
    utilisationMode === 'timesheet'
      ? 'Billable hours % last 30 days'
      : 'Planned utilisation next 4 weeks';

  // Live mode takes the department breakdown, the blended figure and the bench
  // count from /api/dashboard/utilisation/summary, which computes them over the
  // same per-employee rows this used to download. Demo/tour derives them from
  // the mock rows.
  type DeptUtilisation = { department: string; utilisationPercent: number; headcount: number; status: 'under' | 'target' | 'over' };
  const deptMap = new Map<string, { total: number; count: number; statusCounts: Record<string, number> }>();
  utilisationData.forEach(u => {
    const key = u.department || 'Unknown';
    const existing = deptMap.get(key) ?? { total: 0, count: 0, statusCounts: { under: 0, target: 0, over: 0 } };
    existing.total += u.utilisationPercent;
    existing.count += 1;
    existing.statusCounts[u.status] = (existing.statusCounts[u.status] ?? 0) + 1;
    deptMap.set(key, existing);
  });
  const derivedDeptUtilisation: DeptUtilisation[] = Array.from(deptMap.entries()).map(([department, agg]) => {
    const utilisationPercent = agg.count > 0 ? Math.round(agg.total / agg.count) : 0;
    // Majority-vote status for the department
    const dominantStatus = (
      ['target', 'over', 'under'] as const
    ).reduce((best, s) =>
      (agg.statusCounts[s] ?? 0) > (agg.statusCounts[best] ?? 0) ? s : best,
      'target' as 'under' | 'target' | 'over',
    );
    return { department, utilisationPercent, headcount: agg.count, status: dominantStatus };
  }).sort((a, b) => b.utilisationPercent - a.utilisationPercent);

  const deptUtilisation: DeptUtilisation[] = utilisationSummary
    ? [...utilisationSummary.departments]
        .map(d => ({ department: d.department, utilisationPercent: d.utilisationPercent, headcount: d.headcount, status: d.status }))
        .sort((a, b) => b.utilisationPercent - a.utilisationPercent)
    : derivedDeptUtilisation;

  // Employees behind the utilisation figures — 0 means "no utilisation data".
  const utilisationEmployeeCount = utilisationSummary
    ? utilisationSummary.employeeCount
    : utilisationData.length;

  // Blended portfolio utilisation %
  const blendedUtilisationPct = utilisationSummary
    ? utilisationSummary.blendedUtilisationPercent
    : utilisationData.length > 0
      ? Math.round(utilisationData.reduce((s, u) => s + u.utilisationPercent, 0) / utilisationData.length)
      : 0;

  // Bench count: employees under target by >10pp
  const benchCount = utilisationSummary
    ? utilisationSummary.underUtilisedCount
    : utilisationData.filter(u => u.status === 'under').length;

  // In live mode, use real prior-month utilisation from date-range endpoint.
  // Falls back to KPI snapshot if date-range data unavailable, then to 0 delta.
  // In demo/tour, use ×0.96 approximation for visual consistency.
  const priorMonthUtilisation = useMemo(() => {
    if (isLiveMode) {
      if (priorMonthUtilData) return priorMonthUtilData.avgPct;
      if (priorKpiSnapshot?.blendedUtilisationPct != null) return priorKpiSnapshot.blendedUtilisationPct;
      return blendedUtilisationPct;
    }
    return blendedUtilisationPct * 0.96;
  }, [isLiveMode, priorMonthUtilData, priorKpiSnapshot, blendedUtilisationPct]);
  const utilisationTrendDelta = Math.round((blendedUtilisationPct - priorMonthUtilisation) * 10) / 10;

  // Target utilisation % from settings, falling back to the firm-wide default
  const targetUtilisationPct =
    getFinanceSettings().targetUtilisationPercent ?? DEFAULT_TARGET_UTILISATION_PERCENT;

  // Gauge colour zone
  const gaugeColor =
    blendedUtilisationPct > 85
      ? C_DANGER    // over-utilised
      : blendedUtilisationPct >= 65
        ? C_SUCCESS // healthy
        : C_WARNING; // under-utilised

  // ── Finance widget derived data (migrated from FinanceDashboardShell) ────────

  // nowMs is used by both Finance widgets (below) and the existing key dates
  // section further down. Declare it once here so all usages share the same value.
  const nowMs = Date.now();

  // F29 — Finance Pulse KPI derived data
  // In live mode, prefer pre-computed values from finKpis (SQL-level aggregation)
  // over client-side derivation from wipReports/revenueRecognitions.
  const totalWip = isLiveMode ? (finKpis?.totalWip ?? wipView.totalWip) : wipView.totalWip;

  const derivedRevRecMtd = useMemo(
    () => revenueRecognitions.reduce((sum, r) => sum + (r.revenueRecognised ?? 0), 0),
    [revenueRecognitions],
  );
  const revenueRecognisedMtd = isLiveMode
    ? (finKpis?.revenueRecognisedMtd ?? revenueRecognitionSummary?.totals.revenueRecognised ?? 0)
    : derivedRevRecMtd;

  const finOverdueCount = isLiveMode ? (finKpis?.overdueInvoices.count ?? wipView.overdueCount) : wipView.overdueCount;
  const finOverdueAmount = isLiveMode
    ? (finKpis?.overdueInvoices.value ?? wipView.overdueAmount)
    : wipView.overdueAmount;
  const derivedDeferred = useMemo(
    () => revenueRecognitions.reduce((sum, r) => sum + (r.deferredRevenue ?? 0), 0),
    [revenueRecognitions],
  );
  const totalDeferred = isLiveMode
    ? (finKpis?.deferredRevenue ?? revenueRecognitionSummary?.totals.deferredRevenue ?? 0)
    : derivedDeferred;
  // Prior-period finance KPIs — in live mode, use KPI snapshot data from
  // the DashboardKpiSnapshot endpoint. Falls back to current values (0 delta)
  // if no snapshot exists yet. In demo/tour, use seeded approximations.
  const finSeed = isLiveMode ? 0 : activeProjects.reduce((s, p) => s + p.id, 0);
  const priorWip = useMemo(() => {
    if (isLiveMode) {
      return priorKpiSnapshot?.totalWip ?? totalWip;
    }
    return totalWip * (1 - ((finSeed % 11) - 5) * 0.01);
  }, [isLiveMode, priorKpiSnapshot, totalWip, finSeed]);
  const priorRevRecognised = useMemo(() => {
    if (isLiveMode && monthlyPortfolioData.length >= 2) {
      return monthlyPortfolioData[monthlyPortfolioData.length - 2].periodSales;
    }
    if (isLiveMode && priorKpiSnapshot?.revenueRecognisedMtd != null) {
      return priorKpiSnapshot.revenueRecognisedMtd;
    }
    if (isLiveMode) return revenueRecognisedMtd;
    return revenueRecognisedMtd * (1 - ((finSeed % 7) - 3) * 0.015);
  }, [isLiveMode, monthlyPortfolioData, priorKpiSnapshot, revenueRecognisedMtd, finSeed]);
  const priorFinOverdueCount = useMemo(() => {
    if (isLiveMode) {
      return priorKpiSnapshot?.overdueInvoiceCount ?? finOverdueCount;
    }
    return Math.max(0, finOverdueCount + ((finSeed % 3) - 1));
  }, [isLiveMode, priorKpiSnapshot, finOverdueCount, finSeed]);
  const priorDeferred = useMemo(() => {
    if (isLiveMode) {
      return priorKpiSnapshot?.deferredRevenue ?? totalDeferred;
    }
    return totalDeferred * (1 - ((finSeed % 9) - 4) * 0.01);
  }, [isLiveMode, priorKpiSnapshot, totalDeferred, finSeed]);

  // F30 — WIP Aging Overview.
  // Band totals come from the WIP report, which bands each unbilled ledger entry by its own
  // transaction date, so a project whose WIP spans months contributes to several bands —
  // exactly the split shown on the project Billing tab.
  const portfolioWipBands = wipView.agingBands;
  const portfolioWipTotal = wipView.positiveWipTotal;

  // F31 — Revenue Recognition Status derived data.
  // Live mode renders the server's portfolio aggregates; demo/tour derives the same figures
  // from the mock per-project rows.
  const revTotals = useMemo(() => {
    if (isLiveMode) {
      const t = revenueRecognitionSummary?.totals;
      return {
        activeProjectCount: Number(t?.activeProjectCount ?? 0),
        recognised:  t?.revenueRecognised ?? 0,
        toRecognise: t?.revenueToRecognise ?? 0,
        deferred:    t?.deferredRevenue ?? 0,
        unbilled:    t?.unbilledRevenue ?? 0,
      };
    }
    return {
      activeProjectCount: revenueRecognitions.length,
      recognised:  revenueRecognitions.reduce((s, r) => s + r.revenueRecognised, 0),
      toRecognise: revenueRecognitions.reduce((s, r) => s + r.revenueToRecognise, 0),
      deferred:    revenueRecognitions.reduce((s, r) => s + r.deferredRevenue, 0),
      unbilled:    revenueRecognitions.reduce((s, r) => s + r.unbilledRevenue, 0),
    };
  }, [isLiveMode, revenueRecognitionSummary, revenueRecognitions]);

  const revTotalRecognised = revTotals.recognised;
  const revToRecognise     = revTotals.toRecognise;
  const revDeferred        = revTotals.deferred;
  const revUnbilled        = revTotals.unbilled;
  const revGrandTotal      = revTotalRecognised + revToRecognise + revDeferred + revUnbilled;
  const revActiveProjectCount = revTotals.activeProjectCount;

  const methodBreakdown = useMemo(() => {
    const rowsByMethod = new Map(
      isLiveMode
        ? (revenueRecognitionSummary?.methods ?? []).map(m => [m.method, {
            count: Number(m.projectCount),
            budgetRevenue: m.contractValue,
            revenueRecognised: m.revenueRecognised,
            deferredRevenue: m.deferredRevenue,
            unbilledRevenue: m.unbilledRevenue,
            recognitionPct: m.recognitionPercent,
          }])
        : RECOGNITION_METHODS.map(method => {
            const rows = revenueRecognitions.filter(r => r.method === method);
            const budgetRevenue = rows.reduce((s, r) => s + r.budgetRevenue, 0);
            const revenueRecognised = rows.reduce((s, r) => s + r.revenueRecognised, 0);
            return [method as string, {
              count: rows.length,
              budgetRevenue,
              revenueRecognised,
              deferredRevenue: rows.reduce((s, r) => s + r.deferredRevenue, 0),
              unbilledRevenue: rows.reduce((s, r) => s + r.unbilledRevenue, 0),
              recognitionPct: budgetRevenue > 0 ? (revenueRecognised / budgetRevenue) * 100 : 0,
            }];
          }),
    );
    return RECOGNITION_METHODS
      .map(method => ({ method, label: METHOD_LABELS[method], ...(rowsByMethod.get(method) ?? { count: 0, budgetRevenue: 0, revenueRecognised: 0, deferredRevenue: 0, unbilledRevenue: 0, recognitionPct: 0 }) }))
      .filter(m => m.count > 0);
  }, [isLiveMode, revenueRecognitionSummary, revenueRecognitions]);

  const monthEndDaysToClose = monthEnd.daysToClose ?? 99;
  const revActionList = useMemo(() => {
    const items: { text: string; urgency: 'red' | 'amber' | 'blue' }[] = [];
    if (revToRecognise > 0) {
      items.push({
        text: `${fmtCurrency(revToRecognise)} to recognise before month-end`,
        urgency: monthEndDaysToClose <= 5 ? 'red' : 'amber',
      });
    }
    if (isLiveMode) {
      for (const a of revenueRecognitionSummary?.attention ?? []) {
        items.push({
          text: a.reason === 'deferred-over-20-percent'
            ? `${a.projectName}: deferred revenue exceeds 20% of contract`
            : `${a.projectName}: ${a.completionPercent.toFixed(0)}% complete but only ${a.recognitionPercent.toFixed(0)}% recognised`,
          urgency: 'amber',
        });
      }
    } else {
      revenueRecognitions.forEach(r => {
        if (r.deferredRevenue > r.budgetRevenue * 0.2) {
          items.push({ text: `${r.projectName}: deferred revenue exceeds 20% of contract`, urgency: 'amber' });
        }
        const recognitionPct = r.budgetRevenue > 0 ? (r.revenueRecognised / r.budgetRevenue) * 100 : 0;
        if (r.completionPercent - recognitionPct > 15) {
          items.push({ text: `${r.projectName}: ${r.completionPercent.toFixed(0)}% complete but only ${recognitionPct.toFixed(0)}% recognised`, urgency: 'amber' });
        }
      });
    }
    return items.slice(0, 5);
  }, [isLiveMode, revenueRecognitionSummary, revenueRecognitions, revToRecognise, monthEndDaysToClose]);

  // F32 — Billing Pipeline state
  const [billingQueueTab, setBillingQueueTab] = useState<'ready' | 'coming' | 'overdue'>('ready');
  const MAX_QUEUE_ROWS = 8;

  // F33 — Month-End Readiness derived data
  const finHasWipOver60        = wipView.agedOverThresholdCount > 0;
  const finHasReadyToBill      = wipView.readyToBillCount > 0;
  const finHasOverdueMilestone = finOverdueCount > 0;
  const finHasRevToRecognise   = revToRecognise > 0;
  const finHasOverBilling      = wipView.overBilledCount > 0;
  const finHasWrittenOff       = wipView.writtenOffCount > 0;
  const finHasFinanceSync      = financeStatus.isConnected;
  const finHasTimesheetDraft   = !timesheetStatus.isConnected;

  type ChecklistState = 'complete' | 'action' | 'blocked';
  interface ChecklistItem {
    label: string;
    state: ChecklistState;
    detail?: string;
    actionRoute?: string;
  }
  const checklistItems: ChecklistItem[] = useMemo(() => [
    {
      label: 'Review all WIP amounts',
      state: finHasWipOver60 ? 'action' : 'complete',
      detail: finHasWipOver60 ? `${wipView.agedOverThresholdCount} project(s) with WIP aged ${wipThresholds.agedWipDaysThreshold}+ days` : undefined,
      actionRoute: 'wip-reporting',
    },
    {
      label: 'Process ready-to-bill projects',
      state: finHasReadyToBill ? 'action' : 'complete',
      detail: finHasReadyToBill ? `${wipView.readyToBillCount} project(s) ready to raise` : undefined,
      actionRoute: 'wip-reporting',
    },
    {
      label: 'Resolve overdue billing milestones',
      state: finHasOverdueMilestone ? 'action' : 'complete',
      detail: finHasOverdueMilestone ? `${finOverdueCount} overdue milestone${finOverdueCount !== 1 ? 's' : ''}` : undefined,
      actionRoute: 'reports/wip',
    },
    {
      label: 'Review revenue recognition',
      state: finHasRevToRecognise ? 'action' : 'complete',
      detail: finHasRevToRecognise ? `${fmtCurrency(revToRecognise)} to recognise` : undefined,
      actionRoute: 'reports/profitability',
    },
    {
      label: 'Clear over-billing',
      state: finHasOverBilling ? 'action' : 'complete',
      detail: finHasOverBilling ? `${wipView.overBilledCount} project(s) over-billed` : undefined,
      actionRoute: 'wip-reporting',
    },
    {
      label: 'Review write-offs',
      state: finHasWrittenOff ? 'action' : 'complete',
      detail: finHasWrittenOff ? `${wipView.writtenOffCount} project(s) with write-offs` : undefined,
      actionRoute: 'reports/wip',
    },
    {
      label: 'Reconcile with Finance system',
      state: finHasFinanceSync ? 'complete' : 'blocked',
      detail: finHasFinanceSync ? undefined : 'Finance system not connected',
      actionRoute: 'settings/integrations',
    },
    {
      label: 'Verify timesheet approvals',
      state: finHasTimesheetDraft ? 'blocked' : 'complete',
      detail: finHasTimesheetDraft ? 'Timesheet system not connected' : undefined,
      actionRoute: 'timesheets',
    },
  ], [
    finHasWipOver60, finHasReadyToBill, finHasOverdueMilestone, finHasRevToRecognise,
    finHasOverBilling, finHasWrittenOff, finHasFinanceSync, finHasTimesheetDraft,
    finOverdueCount, revToRecognise, wipThresholds.agedWipDaysThreshold,
    wipView.agedOverThresholdCount, wipView.readyToBillCount,
    wipView.overBilledCount, wipView.writtenOffCount,
  ]);
  const finCompleteCount  = checklistItems.filter(i => i.state === 'complete').length;
  const finProgressPct    = Math.round((finCompleteCount / checklistItems.length) * 100);

  // F35 — Over & Under Billing derived data
  const totalOverBilled  = wipView.overBilledAmount;
  const totalUnderBilled = wipView.underBilledAmount;

  // F36 — Project Financial Summary state (server-side paginated)
  const {
    rows: summaryPagedRows,
    totalCount: summaryTotalCount,
    page: summaryPage,
    setPage: setSummaryPage,
    pageSize: SUMMARY_PAGE_SIZE,
    pageCount: summaryPageCount,
    filters: financeFilter,
    updateFilters: updateFinanceFilter,
    loading: summaryLoading,
  } = usePagedFinancialSummary(revenueRecognitions, isLiveMode, currentCompany?.id);
  const [showPm, setShowPm]                   = useState(true);
  const [showType, setShowType]               = useState(true);
  const [showMethod, setShowMethod]           = useState(false);
  const [showBudgetRev, setShowBudgetRev]     = useState(false);
  const [showActualSales, setShowActualSales] = useState(true);
  const [showRevVar, setShowRevVar]           = useState(false);
  const [showActualCosts, setShowActualCosts] = useState(false);
  const [showCompletion, setShowCompletion]   = useState(true);
  const [showHealth, setShowHealth]           = useState(true);
  const [showFilters, setShowFilters]         = useState(false);

  const filteredSummaryRows = summaryPagedRows;

  // ── Shared Finance helper functions ───────────────────────────────────────────
  function fmtCurrency(v: number) {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
  }
  function fmtDate(iso: string | null | undefined) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  function daysUntil(iso: string) {
    return Math.ceil((new Date(iso).getTime() - nowMs) / (1000 * 60 * 60 * 24));
  }
  function AgingBadge({ days }: { days: number }) {
    if (days <= 30)  return <Badge variant="outline" className="text-xs border-success-200 text-success-700 bg-success-50">0–30d</Badge>;
    if (days <= 60)  return <Badge variant="outline" className="text-xs border-warning-200 text-warning-700 bg-warning-50">31–60d</Badge>;
    if (days <= 90)  return <Badge variant="outline" className="text-xs border-orange-200 text-orange-700 bg-orange-50">61–90d</Badge>;
    if (days <= 120) return <Badge variant="outline" className="text-xs border-error-200 text-error-600 bg-error-50">91–120d</Badge>;
    return <Badge variant="outline" className="text-xs border-destructive/40 text-destructive bg-destructive/5">121+d</Badge>;
  }
  function FinRagBadge({ health }: { health: string }) {
    const map: Record<string, { label: string; className: string }> = {
      green: { label: 'Healthy',  className: 'bg-success-100 text-success-700 border-success-200' },
      amber: { label: 'At Risk',  className: 'bg-warning-100 text-warning-700 border-warning-200' },
      red:   { label: 'Critical', className: 'bg-destructive/10 text-destructive border-destructive/20' },
    };
    const cfg = map[health] ?? { label: 'Unknown', className: 'bg-muted text-muted-foreground border-border' };
    return <Badge variant="outline" className={cn('text-xs font-medium whitespace-nowrap', cfg.className)}>{cfg.label}</Badge>;
  }
  function SignalChip({ label, colour }: { label: string; colour: string }) {
    const colourMap: Record<string, string> = {
      destructive: 'bg-destructive/10 text-destructive border-destructive/20',
      warning:     'bg-warning-100 text-warning-700 border-warning-200',
      blue:        'bg-blue-100 text-blue-700 border-blue-200',
    };
    return <Badge variant="outline" className={cn('text-xs font-medium whitespace-nowrap', colourMap[colour] ?? 'bg-muted text-muted-foreground')}>{label}</Badge>;
  }
  function monthEndBorderClass() {
    if (monthEnd.urgency === 'red')   return 'border-error-400 dark:border-error-600';
    if (monthEnd.urgency === 'amber') return 'border-warning-400 dark:border-warning-500';
    return '';
  }
  function MonthEndBanner() {
    if (monthEnd.urgency === 'normal' || !monthEnd.bannerText) return null;
    const isRed = monthEnd.urgency === 'red';
    return (
      <div className={cn(
        'flex items-center gap-2 rounded-md px-3 py-2 text-xs mb-3',
        isRed
          ? 'bg-error-50 dark:bg-error-950/20 border border-error-200 dark:border-error-800 text-error-700 dark:text-error-300'
          : 'bg-warning-50 dark:bg-warning-950/20 border border-warning-200 dark:border-warning-800 text-warning-700 dark:text-warning-300',
      )}>
        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
        {monthEnd.bannerText}
      </div>
    );
  }

  // ── End Finance widget derived data ───────────────────────────────────────────

  const atRiskWithInsights = atRiskProjects.map(project => {
    const insights: { type: string; severity: string; message: string }[] = [];
    const recommendations: string[] = [];

    if (project.costVariancePercent < -5) {
      insights.push({
        type: 'budget',
        severity: project.costVariancePercent < -10 ? 'critical' : 'warning',
        message: `${formatPercent(Math.abs(project.costVariancePercent))} over cost budget (${formatCurrency(Math.abs(project.costVariance))})`,
      });
      if (project.hoursVariancePercent > 5) {
        insights.push({
          type: 'hours',
          severity: 'warning',
          message: `Labour hours ${formatPercent(project.hoursVariancePercent)} over plan`,
        });
        recommendations.push('Reduce team size by 1 FTE or renegotiate scope');
      }
    }
    if (project.marginPercent < 15) {
      insights.push({
        type: 'margin',
        severity: project.marginPercent < 10 ? 'critical' : 'warning',
        message: `Low margin: ${formatPercent(project.marginPercent)} (target: 30%)`,
      });
      recommendations.push('Increase billing rate or reduce costs');
    }
    const startDate = new Date(project.startDate);
    const endDate = new Date(project.endDate);
    const today = new Date();
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = today.getTime() - startDate.getTime();
    const expectedProgress = (elapsed / totalDuration) * 100;
    if (project.completionPercent < expectedProgress - 10) {
      insights.push({
        type: 'timeline',
        severity: 'warning',
        message: `Behind schedule: ${formatPercent(project.completionPercent)} complete vs ${formatPercent(expectedProgress)} expected`,
      });
      recommendations.push('Review project plan and adjust timeline');
    }
    if (project.wipAmount > wipThresholds.agedWipAmountAlert) {
      insights.push({
        type: 'wip',
        severity: 'warning',
        message: `High WIP: ${formatCurrency(project.wipAmount)} unbilled`,
      });
      recommendations.push('Accelerate invoicing to improve cash flow');
    }

    return { ...project, insights, recommendations: recommendations.slice(0, 2) };
  });

  // WIP aged over 60 days — used by F13 (Billing & Cash Flow Health).
  const agedOver60 = portfolioWipBands['61-90'] + portfolioWipBands['91-120'] + portfolioWipBands['121+'];

  // ── F13 — Billing & Cash Flow Health derived data ──────────────────────────

  // Component B: Invoice Pipeline. In live mode the figures come from
  // /api/dashboard/billing-pipeline, which aggregates billing milestones,
  // agreed retainer amounts and unbilled WIP server-side. Demo/tour keep the
  // WIPReport-derived approximation because mock data has no pipeline endpoint.
  // nowMs is declared above in the Finance widget derived data section.

  // Categorise WIP reports by project type
  const fixedReports = wipReports.filter(r => r.projectType === 'fixed-price');
  const tamReports   = wipReports.filter(r => r.projectType === 'time-and-materials' || r.projectType === 'retainer');

  // Helper: is a date string in "this month"?
  function isThisMonth(dateStr?: string): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const n = new Date();
    return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
  }
  // Helper: is a date string in "next month"?
  function isNextMonth(dateStr?: string): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const n = new Date();
    const nm = new Date(n.getFullYear(), n.getMonth() + 1, 1);
    return d.getFullYear() === nm.getFullYear() && d.getMonth() === nm.getMonth();
  }
  // Helper: is a date string overdue (past today)?
  function isPastDue(dateStr?: string): boolean {
    if (!dateStr) return false;
    return new Date(dateStr).getTime() < nowMs;
  }

  // Fixed-price pipeline from nextBillingEvent dates (demo/tour)
  const derivedFpThisMonth = fixedReports.filter(r => r.nextBillingEvent && isThisMonth(r.nextBillingEvent) && !isPastDue(r.nextBillingEvent)).reduce((s, r) => s + (r.nextBillingEventAmount ?? 0), 0);
  const derivedFpNextMonth = fixedReports.filter(r => r.nextBillingEvent && isNextMonth(r.nextBillingEvent)).reduce((s, r) => s + (r.nextBillingEventAmount ?? 0), 0);
  const derivedFpOverdue   = fixedReports.filter(r => r.nextBillingEvent && isPastDue(r.nextBillingEvent)).reduce((s, r) => s + (r.nextBillingEventAmount ?? 0), 0);

  // T&M pipeline estimated from prior-month actuals — approximate from WIP reports
  const tamMonthlyRunRate = tamReports.reduce((s, r) => s + (r.costsIncurred > 0 ? r.costsIncurred / 12 : 0), 0);
  // T&M overdue: WIP aged > 60 days that is "raised but unpaid" — proxy with aged WIP
  const derivedTamOverdue = tamReports.filter(r => (r.agingDays ?? 0) > wipThresholds.agedWipDaysThreshold).reduce((s, r) => s + r.wipAmount, 0);

  const livePipeline = isLiveMode ? billingPipeline : null;

  const fpThisMonth = livePipeline?.fixedPrice.thisMonth.amount ?? (isLiveMode ? 0 : derivedFpThisMonth);
  const fpNextMonth = livePipeline?.fixedPrice.nextMonth.amount ?? (isLiveMode ? 0 : derivedFpNextMonth);
  const fpOverdue   = livePipeline?.fixedPrice.overdue.amount   ?? (isLiveMode ? 0 : derivedFpOverdue);
  const tamThisMonth = livePipeline?.timeAndMaterials.thisMonth.amount ?? (isLiveMode ? 0 : tamMonthlyRunRate);
  const tamNextMonth = livePipeline?.timeAndMaterials.nextMonth.amount ?? (isLiveMode ? 0 : tamMonthlyRunRate);
  const tamOverdue   = livePipeline?.timeAndMaterials.overdue.amount   ?? (isLiveMode ? 0 : derivedTamOverdue);

  // Only the forecast share of next month's T&M figure is an estimate; agreed
  // retainer amounts, unbilled WIP and overdue milestones are actuals.
  const tamThisMonthIsEstimate = livePipeline == null;
  const tamNextMonthIsEstimate = livePipeline == null || livePipeline.timeAndMaterials.nextMonth.forecastAmount > 0;
  const tamOverdueIsEstimate   = livePipeline == null;

  // Component C: Cash Flow Health Score
  // Healthy → no overdue invoices, WIP mostly current (<20% aged 60+)
  // Watch   → some overdue or 20–40% aged 60+
  // At Risk → significant overdue or >40% aged 60+
  const aged60PlusFraction = portfolioWipTotal > 0 ? agedOver60 / portfolioWipTotal : 0;
  const hasOverdue = fpOverdue > 0 || tamOverdue > 0;
  const cashFlowHealthScore: 'healthy' | 'watch' | 'at-risk' =
    aged60PlusFraction > 0.4 || (fpOverdue + tamOverdue) > 50000
      ? 'at-risk'
      : aged60PlusFraction > 0.2 || hasOverdue
        ? 'watch'
        : 'healthy';

  const cashFlowHealthConfig = {
    healthy: { label: 'Healthy',  icon: ShieldCheck,  bg: 'bg-success-50',      text: 'text-success-700',   border: 'border-success-200'  },
    watch:   { label: 'Watch',    icon: ShieldAlert,  bg: 'bg-warning-50',       text: 'text-warning-700',    border: 'border-warning-200'   },
    'at-risk': { label: 'At Risk', icon: ShieldX,     bg: 'bg-destructive/5',   text: 'text-destructive',   border: 'border-destructive/20' },
  } as const;

  // ── F14 — Client Concentration Risk ──────────────────────────────────────────
  // PRD §3 Insight 7 — remaining contract value per client, concentration badge.
  // "Remaining Contract Value" = budgetRevenue - actualSales (floor 0).
  // Groups by primary client name (first entry in clientNames CSV).
  // Configured threshold from settings, falling back to the firm-wide default.
  const concentrationThresholdPercent =
    getFinanceSettings().concentrationThresholdPercent ?? DEFAULT_CONCENTRATION_THRESHOLD_PERCENT;
  const concentrationThresholdPct = concentrationThresholdPercent / 100;

  // Build per-client RCV aggregation from active projects
  type ClientRcvRow = {
    clientName: string;
    rcv: number;
    projectCount: number;
    pct: number;
    isOther?: boolean;
  };

  const rcvByClient: Record<string, { rcv: number; count: number }> = {};
  if (isLiveMode) {
    // Live figures come from the bundle's clientConcentration section, which groups by the
    // actual client relationship, so a project shared between clients contributes to each.
    for (const c of clientConcentration?.clients ?? []) {
      rcvByClient[c.clientName] = { rcv: c.remainingContractValue, count: c.projectCount };
    }
  } else {
    for (const p of activeProjects) {
      const primaryClient = p.clientNames ? p.clientNames.split(',')[0].trim() : 'Unknown';
      const rcv = Math.max(0, (p.budgetRevenue || 0) - (p.actualSales || 0));
      if (!rcvByClient[primaryClient]) rcvByClient[primaryClient] = { rcv: 0, count: 0 };
      rcvByClient[primaryClient].rcv  += rcv;
      rcvByClient[primaryClient].count += 1;
    }
  }

  // The tail the server already collapsed, kept out of the per-client rollup so it cannot
  // count as a client in the concentration badge.
  const serverOtherClients = isLiveMode ? clientConcentration?.other ?? null : null;

  const totalPortfolioRcv = isLiveMode
    ? (clientConcentration?.totalRemainingValue ?? 0)
    : Object.values(rcvByClient).reduce((s, r) => s + r.rcv, 0);

  // Sort descending by RCV
  const sortedClientRcv: ClientRcvRow[] = Object.entries(rcvByClient)
    .map(([clientName, { rcv, count }]) => ({
      clientName,
      rcv,
      projectCount: count,
      pct: totalPortfolioRcv > 0 ? rcv / totalPortfolioRcv : 0,
      isOther: false,
    }))
    .sort((a, b) => b.rcv - a.rcv);

  // Max 8 clients — collapse remainder into "Other clients (n)"
  const MAX_CLIENT_BARS = 8;
  let clientRcvRows: ClientRcvRow[];
  if (sortedClientRcv.length <= MAX_CLIENT_BARS) {
    clientRcvRows = serverOtherClients
      ? [
          ...sortedClientRcv,
          {
            clientName: `Other clients (${serverOtherClients.count})`,
            rcv: serverOtherClients.remainingContractValue,
            projectCount: serverOtherClients.count,
            pct: totalPortfolioRcv > 0 ? serverOtherClients.remainingContractValue / totalPortfolioRcv : 0,
            isOther: true,
          },
        ]
      : sortedClientRcv;
  } else {
    const top8 = sortedClientRcv.slice(0, MAX_CLIENT_BARS);
    const others = sortedClientRcv.slice(MAX_CLIENT_BARS);
    const otherRcv   = others.reduce((s, r) => s + r.rcv, 0);
    const otherCount = others.reduce((s, r) => s + r.projectCount, 0);
    clientRcvRows = [
      ...top8,
      {
        clientName: `Other clients (${others.length})`,
        rcv: otherRcv,
        projectCount: otherCount,
        pct: totalPortfolioRcv > 0 ? otherRcv / totalPortfolioRcv : 0,
        isOther: true,
      },
    ];
  }

  // Health badge — based on single highest-concentration client (excluding "Other")
  const topClientPct = sortedClientRcv[0]?.pct ?? 0;
  const hasEnoughClients = sortedClientRcv.length >= 3; // suppress warning if < 3 clients
  type ConcentrationHealth = 'well-diversified' | 'moderate' | 'high' | 'critical';
  const concentrationHealth: ConcentrationHealth = !hasEnoughClients
    ? 'well-diversified'
    : topClientPct > 0.7
      ? 'critical'
      : topClientPct > 0.5
        ? 'high'
        : topClientPct > concentrationThresholdPct
          ? 'moderate'
          : 'well-diversified';

  const concentrationHealthConfig: Record<ConcentrationHealth, { label: string; bg: string; text: string; border: string }> = {
    'well-diversified': { label: 'Well Diversified', bg: 'bg-success-50',    text: 'text-success-700',   border: 'border-success-200'   },
    'moderate':         { label: 'Moderate Concentration', bg: 'bg-warning-50',  text: 'text-warning-700',    border: 'border-warning-200'    },
    'high':             { label: 'High Concentration',     bg: 'bg-orange-50',  text: 'text-orange-700',    border: 'border-orange-200'    },
    'critical':         { label: 'Critical Dependency',    bg: 'bg-destructive/5', text: 'text-destructive', border: 'border-destructive/20' },
  };

  // Top 3 clients' combined % (for summary sentence)
  const top3Pct = sortedClientRcv.slice(0, 3).reduce((s, r) => s + r.pct, 0);

  // ── F9 — Projects Requiring Attention: Root Cause Tags + table rows ──────────
  // PRD §3 Insight 2 — four root cause tags derived from available data.
  // Sorted: red first, then amber, then by number of root causes desc (most urgent first).
  // Max 5 rows in the widget; "View all" link shown if more exist.

  type RootCauseTag = 'Over Budget' | 'Delayed' | 'Understaffed' | 'Unbilled WIP';

  function deriveRootCauseTags(
    project: (typeof atRiskProjects)[number],
    today: Date,
  ): RootCauseTag[] {
    const tags: RootCauseTag[] = [];

    // Over Budget — cost variance >10% over (negative variance = over budget)
    if (project.costVariancePercent < -10) tags.push('Over Budget');

    // Delayed — endDate is in the past and project is still active
    if (project.endDate && new Date(project.endDate) < today) tags.push('Delayed');

    // Understaffed — no roleAssignments configured (no planner data available yet)
    // Engineering note: when planner integration is live, replace with
    // resource gap data from the planner API (GET /api/planner/resource-gaps?projectId=X)
    if (project.roleAssignments && project.roleAssignments.length === 0) {
      tags.push('Understaffed');
    }

    // Unbilled WIP — WIPReport.agingDays > 60 for this project
    const projectWip = wipReports.find(w => w.projectId === project.id);
    if (projectWip && projectWip.agingDays > wipThresholds.agedWipDaysThreshold) tags.push('Unbilled WIP');

    return tags;
  }

  const today = new Date();

  // In live mode, at-risk rows come pre-enriched from the backend endpoint.
  // In demo/tour, derive from client-side data.
  const atRiskRows = isLiveMode
    ? (atRiskSummary?.rows ?? []).map(p => ({
        project: { ...p, id: p.id, name: p.name, health: p.health, costVariancePercent: p.costVariancePercent, costVariance: p.costVariance, budgetRevenue: p.budgetRevenue, completionPercent: p.completionPercent, clientNames: p.clientNames },
        rootCauseTags: p.rootCauseTags,
        pm: p.pmName ? { name: p.pmName } : undefined,
        client: p.clientNames ? { name: p.clientNames.split(',')[0].trim() } : undefined,
        daysOverdue: p.daysOverdue > 0 ? p.daysOverdue : null,
      }))
    : [...atRiskProjects]
        .sort((a, b) => {
          if (a.health === 'red' && b.health !== 'red') return -1;
          if (b.health === 'red' && a.health !== 'red') return 1;
          return a.costVariancePercent - b.costVariancePercent;
        })
        .map(project => {
          const rootCauseTags = deriveRootCauseTags(project, today);
          const pm = employees.find(e => e.id === project.projectManagerId);
          const firstClientId = project.clientIds?.split(',')[0];
          const client = firstClientId ? clients.find(c => c.id === Number(firstClientId)) : undefined;
          const endDate = project.endDate ? new Date(project.endDate) : null;
          const daysOverdue = endDate && endDate < today
            ? Math.floor((today.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24))
            : null;
          return { project, rootCauseTags, pm, client, daysOverdue };
        });

  const atRiskRowsVisible = isLiveMode
    ? atRiskRows
    : atRiskRows.slice(0, AT_RISK_DEMO_VISIBLE_ROWS);
  const atRiskTotal = isLiveMode
    ? (atRiskSummary?.counts.totalCount ?? 0)
    : atRiskRows.length;
  const atRiskOverflow = Math.max(atRiskTotal - atRiskRowsVisible.length, 0);

  // ── F15 — Upcoming Key Dates ─────────────────────────────────────────────────
  // PRD §3, Insight 8 — Chronological prioritised list of events in next 30 days.
  // Three row types:
  //   1. Billing milestone due  — from wipReports.nextBillingEvent (fixed-price)
  //   2. Project end approaching — projects whose endDate is within 30 days
  //   3. Schedule overrun flagged — forecastEndDate > endDate
  //
  // Urgency: Overdue (red), This week (amber), This month (blue)
  // Max 10 rows shown; overflow count link to /projects.
  // forecastEndDate column only renders if at least one project has it set.

  type KeyDateUrgency = 'overdue' | 'this-week' | 'this-month';
  type KeyDateType = 'billing-milestone' | 'project-end' | 'schedule-overrun';

  interface KeyDateRow {
    id: string;
    date: Date;
    type: KeyDateType;
    urgency: KeyDateUrgency;
    projectId: number;
    projectName: string;
    clientName: string;
    amount?: number;          // billing milestones only
    forecastEndDate?: Date;   // schedule overrun only
  }

  function getKeyDateUrgency(date: Date, referenceDate: Date): KeyDateUrgency {
    const diffMs  = date.getTime() - referenceDate.getTime();
    const diffDay = diffMs / (1000 * 60 * 60 * 24);
    if (diffDay < 0)   return 'overdue';
    if (diffDay <= 7)  return 'this-week';
    return 'this-month';
  }

  const todayStart = new Date(today);
  todayStart.setHours(0, 0, 0, 0);
  const in30Days = new Date(todayStart);
  in30Days.setDate(in30Days.getDate() + 30);

  const keyDateRows: KeyDateRow[] = [];

  if (isLiveMode && keyDates.length > 0) {
    // Live mode: use pre-aggregated key dates from the backend endpoint
    const TYPE_MAP: Record<string, KeyDateType> = {
      milestone_due: 'billing-milestone',
      project_ending: 'project-end',
      forecast_overrun: 'schedule-overrun',
    };
    const SEVERITY_MAP: Record<string, KeyDateUrgency> = {
      urgent: 'overdue',
      warning: 'this-week',
      info: 'this-month',
    };
    for (const ev of keyDates) {
      const d = new Date(ev.date);
      keyDateRows.push({
        id:          `${ev.type}-${ev.projectId ?? 0}`,
        date:        d,
        type:        TYPE_MAP[ev.type] ?? 'project-end',
        urgency:     SEVERITY_MAP[ev.severity] ?? getKeyDateUrgency(d, todayStart),
        projectId:   ev.projectId ?? 0,
        projectName: ev.projectName ?? 'Unknown',
        clientName:  ev.clientNames?.split(',')[0].trim() ?? 'Unknown',
        amount:      ev.amount ?? undefined,
      });
    }
  } else {
    // Demo/tour mode: derive client-side from wipReports + activeProjects

    // Row type 1 — Billing milestones from wipReports (fixed-price, date-based)
    for (const wip of wipReports) {
      if (!wip.nextBillingEvent) continue;
      if (wip.nextBillingEventTriggerType === 'percent-complete') continue; // no fixed date
      const d = new Date(wip.nextBillingEvent);
      if (d > in30Days) continue; // beyond 30-day window
      const urgency = getKeyDateUrgency(d, todayStart);
      const clientName = wip.clientName || 'Unknown';
      keyDateRows.push({
        id:          `billing-${wip.projectId}`,
        date:        d,
        type:        'billing-milestone',
        urgency,
        projectId:   wip.projectId,
        projectName: wip.projectName,
        clientName,
        amount:      wip.nextBillingEventAmount,
      });
    }

    // Row type 2 — Project end approaching (endDate within 30 days, not yet overdue)
    // Row type 3 — Schedule overrun flagged (forecastEndDate > endDate)
    for (const p of activeProjects) {
      if (!p.endDate) continue;
      const endD = new Date(p.endDate);
      const primaryClient = p.clientNames?.split(',')[0].trim() || 'Unknown';

      // Schedule overrun — forecastEndDate exists and is after endDate
      if (p.forecastEndDate) {
        const forecastD = new Date(p.forecastEndDate);
        if (forecastD > endD) {
          // Show overrun row if the forecast/end window falls within 30 days from today
          const relevantDate = endD < todayStart ? endD : endD; // anchor to original end
          if (relevantDate <= in30Days) {
            keyDateRows.push({
              id:             `overrun-${p.id}`,
              date:           endD,
              type:           'schedule-overrun',
              urgency:        getKeyDateUrgency(endD, todayStart),
              projectId:      p.id,
              projectName:    p.name,
              clientName:     primaryClient,
              forecastEndDate: forecastD,
            });
            continue; // don't also add a project-end row for same project
          }
        }
      }

      // Project end approaching — endDate within next 30 days (or already overdue)
      if (endD <= in30Days) {
        keyDateRows.push({
          id:          `end-${p.id}`,
          date:        endD,
          type:        'project-end',
          urgency:     getKeyDateUrgency(endD, todayStart),
          projectId:   p.id,
          projectName: p.name,
          clientName:  primaryClient,
        });
      }
    }
  }

  // Sort chronologically (soonest / most overdue first)
  keyDateRows.sort((a, b) => a.date.getTime() - b.date.getTime());

  const MAX_KEY_DATE_ROWS = 10;
  const keyDateRowsVisible = keyDateRows.slice(0, MAX_KEY_DATE_ROWS);
  const keyDateOverflow    = keyDateRows.length > MAX_KEY_DATE_ROWS
    ? keyDateRows.length - MAX_KEY_DATE_ROWS
    : 0;

  // forecastEndDate column only if at least one project has it set
  const hasAnyForecastEnd = keyDateRows.some(r => r.type === 'schedule-overrun' && r.forecastEndDate);

  // Urgency config
  const urgencyConfig: Record<KeyDateUrgency, { label: string; bg: string; text: string; dot: string }> = {
    'overdue':    { label: 'Overdue',     bg: 'bg-destructive/10', text: 'text-destructive',     dot: 'bg-destructive'    },
    'this-week':  { label: 'This Week',   bg: 'bg-warning-100',      text: 'text-warning-700',        dot: 'bg-warning-500'      },
    'this-month': { label: 'This Month',  bg: 'bg-blue-100',       text: 'text-blue-700',         dot: 'bg-blue-500'       },
  };

  // Row type config
  const keyDateTypeConfig: Record<KeyDateType, { label: string; icon: React.ElementType; iconColor: string }> = {
    'billing-milestone': { label: 'Billing Due',      icon: Banknote,      iconColor: 'text-emerald-600' },
    'project-end':       { label: 'Project Ending',   icon: Clock,         iconColor: 'text-blue-600'    },
    'schedule-overrun':  { label: 'Schedule Overrun', icon: Flag,          iconColor: 'text-orange-600'  },
  };

  // ── F16 — Headcount & Delivery Capacity derived data ─────────────────────────
  // PRD §3, Insight 9 — 3 KPI tiles + department capacity bars.
  // Data source: planning tool waterfall (same mode logic as F12 Utilisation Snapshot).
  //
  // KPI tiles:
  //   Total Headcount      — employees where isAvailable = true
  //   Currently Allocated  — employees with at least one utilisationData entry with
  //                          status 'target' or 'over' (planning tool) or >0 utilisationPct
  //   Available Now        — headcount minus fully-allocated
  //
  // Department bars:
  //   Built from deptUtilisation (already derived for F12) — sorted by utilisationPct desc
  //   Colour per status: under=amber, target=green, over=red
  //   Suppressed if < 3 departments (show tiles only)
  //
  // ⚠️  ENGINEERING NOTE: When the backend provides a real
  //   GET /api/dashboard/utilisation/summary endpoint (PRD §7.2), replace the
  //   utilisationData-derived calculations below with the API response which will
  //   include totalHeadcount, currentlyAllocated, availableNow, and
  //   utilisationByDepartment directly. (Live mode now reads the allocated and
  //   department figures from that endpoint; headcount still comes from
  //   /api/employees.)

  // Total headcount — employees flagged as available
  const totalHeadcount = employees.filter(e => e.isAvailable).length;

  // Currently allocated — employees whose utilisation record shows 'target' or 'over'
  // (i.e. they are meaningfully booked). Falls back to utilisationPercent > 20% if no status.
  const currentlyAllocatedCount = utilisationSummary
    ? utilisationSummary.allocatedCount
    : utilisationData.filter(u => u.status === 'target' || u.status === 'over').length;

  // Available Now — headcount not fully allocated (under status or headcount with no record)
  const availableNowCount = Math.max(0, totalHeadcount - currentlyAllocatedCount);

  // Department capacity bars — reuse deptUtilisation already computed for F12
  // Sort descending by utilisationPercent (most stretched first — PRD §3, Insight 9)
  const headcountDeptBars = [...deptUtilisation].sort(
    (a, b) => b.utilisationPercent - a.utilisationPercent,
  );
  const showDeptBars = headcountDeptBars.length >= 3;

  // Colour map per department status
  const deptCapacityColorMap: Record<'under' | 'target' | 'over', { bar: string; text: string; badge: string }> = {
    under:  { bar: 'bg-warning-400',  text: 'text-warning-700',  badge: 'bg-warning-100 text-warning-700'  },
    target: { bar: 'bg-emerald-500',text: 'text-emerald-700',badge: 'bg-emerald-100 text-emerald-700' },
    over:   { bar: 'bg-error-500',    text: 'text-error-700',    badge: 'bg-error-100 text-error-700'       },
  };

  // ── Per-widget data dependency map ──────────────────────────────────────────
  // Maps each widget ID to the data sets it requires.  Widgets whose data has
  // not yet arrived render a lightweight skeleton card instead of blocking the
  // entire dashboard.
  type DataDep = 'kpis' | 'atRisk' | 'profitability' | 'keyDates' | 'clientConcentration' | 'revenueRecognitions' | 'rcbByProject' | 'wip' | 'utilisation' | 'supplement' | 'finKpis' | 'projects';
  const WIDGET_DATA_DEPS: Record<string, DataDep[]> = {
    portfolio_pulse:               ['kpis'],
    projects_requiring_attention:  ['atRisk'],
    upcoming_key_dates:            ['keyDates', 'projects'],
    portfolio_profitability:       ['profitability'],
    revenue_vs_cost_burn:          ['profitability', 'rcbByProject'],
    billing_cash_flow_health:      ['wip', 'projects'],
    client_concentration_risk:     ['clientConcentration', 'projects'],
    utilisation_snapshot:          ['utilisation'],
    headcount_delivery_capacity:   ['utilisation', 'supplement'],
    quick_links_executive:         [],
    finance_pulse:                 ['finKpis', 'wip'],
    wip_aging_overview:            ['wip', 'projects'],
    revenue_recognition_status:    ['revenueRecognitions'],
    billing_pipeline:              ['wip'],
    month_end_readiness:           ['wip', 'finKpis'],
    over_under_billing:            ['wip', 'projects'],
    project_financial_summary:     ['profitability', 'wip', 'projects'],
    // Portfolio Forecast — all mock data, no loading dependencies
    portfolio_forecast:            [],
    // PM cross-persona widgets — data dependencies mirror the PM shell
    pm_pulse:                      ['kpis', 'projects'],
    my_projects_health:            ['projects'],
    budget_hours_burn:             ['projects'],
    schedule_health:               ['projects'],
    my_team_this_week:             ['projects'],
    upcoming_billing_events:       ['wip', 'projects'],
    timesheet_status:              ['projects'],
    quick_links_pm:                [],
  };

  function isWidgetReady(widgetId: string): boolean {
    const deps = WIDGET_DATA_DEPS[widgetId] ?? ['kpis'];
    return deps.every(dep => {
      switch (dep) {
        case 'kpis':                return !kpisLoading;
        case 'atRisk':              return !atRiskLoading;
        case 'profitability':       return !profitabilityLoading;
        case 'keyDates':            return !keyDatesLoading;
        case 'clientConcentration': return !clientConcLoading;
        case 'revenueRecognitions': return !revenueRecLoading;
        case 'rcbByProject':        return !rcbByProjectLoading;
        case 'finKpis':             return !finKpisLoading;
        case 'wip':                 return !wipLoading;
        case 'utilisation':         return !utilisationLoading;
        case 'supplement':          return !supplementLoading;
        case 'projects':            return !projectsDataLoading;
        default:                    return true;
      }
    });
  }

  function WidgetSkeleton({ widgetId }: { widgetId: string }) {
    if (widgetId === 'portfolio_pulse') {
      // Match the number of tiles the widget actually renders so the skeleton
      // doesn't show placeholder tiles for release-hidden tiles.
      const visibleTileCount =
        2 +
        (isPulseTileHiddenForRelease('sales_mtd') ? 0 : 1) +
        (isPulseTileHiddenForRelease('blended_margin') ? 0 : 1);
      const gridColsClass = visibleTileCount >= 4
        ? 'md:grid-cols-2 lg:grid-cols-4'
        : visibleTileCount === 3
          ? 'md:grid-cols-3'
          : 'md:grid-cols-2';
      return (
        <div className={`grid gap-4 ${gridColsClass}`}>
          {Array.from({ length: visibleTileCount }, (_, idx) => idx + 1).map(i => (
            <Card key={i} className="transition-all hover:shadow-md">
              <CardHeader className="pb-2"><Skeleton className="h-4 w-32" /></CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-8 w-24" />
                <Skeleton className="h-3 w-28" />
              </CardContent>
            </Card>
          ))}
        </div>
      );
    }
    return (
      <Card>
        <CardHeader><Skeleton className="h-5 w-48" /></CardHeader>
        <CardContent><Skeleton className="h-48 w-full" /></CardContent>
      </Card>
    );
  }

  // ── Widget renderer ──────────────────────────────────────────────────────────
  // Maps each widget ID to its rendered content.  The orderedVisibleWidgets list
  // from the preferences context drives render order and visibility.

  function renderWidget(widgetId: string) {
    switch (widgetId) {
      // ── Portfolio Pulse (KPI tiles) — F8 ──────────────────────────────────
      // PRD §3, Insight 1 — four tiles: Active Projects, Revenue MTD,
      // Blended Margin %, Projects at Risk.
      case 'portfolio_pulse': {
        const activeCount = isLiveMode
          ? (execKpis?.activeProjects ?? 0)
          : activeProjects.length;
        const atRiskCount = isLiveMode
          ? (execKpis?.projectsAtRisk ?? atRiskSummary?.counts.totalCount ?? 0)
          : atRiskProjects.length;
        const allHealthy = atRiskCount === 0 && activeCount > 0;

        const visibleTileCount =
          2 +
          (isPulseTileHiddenForRelease('sales_mtd') ? 0 : 1) +
          (isPulseTileHiddenForRelease('blended_margin') ? 0 : 1);
        const gridColsClass = visibleTileCount >= 4
          ? 'md:grid-cols-2 lg:grid-cols-4'
          : visibleTileCount === 3
            ? 'md:grid-cols-3'
            : 'md:grid-cols-2';

        return (
          <div key="portfolio_pulse" data-tour="dashboard-kpi-tiles" className={`grid gap-4 ${gridColsClass}`}>

            {/* Tile 1 — Active Projects (info — neutral blue) */}
            <KpiCard
              title="Active Projects"
              value={activeCount}
              icon={FolderKanban}
              description={`${portfolio?.activeProjects ?? activeCount} in portfolio`}
              accentVariant="info"
            />

            {/* Tile 2 — Portfolio Revenue MTD
                RAG: positive trend → success, negative → error.
                Source: revenueRecognitions.revenueRecognised summed across all projects.
                Trend: vs prior month (mock approximation — see engineering note above). */}
            {!isPulseTileHiddenForRelease('sales_mtd') && (
              <KpiCard
                title="Sales MTD"
                value={
                  <SensitiveData>
                    {formatCurrency(revenueMTD)}
                  </SensitiveData>
                }
                icon={Banknote}
                trend={{
                  value: revenueMTDTrend,
                  label: '%',
                  isPositive: revenueMTDTrend >= 0,
                }}
                description="vs prior month"
                accentVariant={
                  revenueMTDTrend >= 5
                    ? 'success'
                    : revenueMTDTrend >= 0
                      ? 'warning'
                      : 'error'
                }
              />
            )}

            {/* Tile 3 — Blended Margin %
                RAG: margin ≥ 20% → success, 10–19% → warning, <10% → error.
                Calculation: (actualSales - actualCosts) / actualSales × 100
                per PRD §2.8 — uses calculateGP(), never actualLabour + actualExpense. */}
            {!isPulseTileHiddenForRelease('blended_margin') && (
              <KpiCard
                title="Blended Margin"
                value={
                  <SensitiveData>
                    {formatPercent(portfolioMargin)}
                  </SensitiveData>
                }
                icon={TrendingUp}
                trend={{
                  value: marginTrend,
                  label: '%',
                  isPositive: marginTrend >= 0,
                }}
                description="Weighted portfolio average"
                accentVariant={
                  portfolioMargin >= 20
                    ? 'success'
                    : portfolioMargin >= 10
                      ? 'warning'
                      : 'error'
                }
              />
            )}

            {/* Tile 4 — Projects at Risk
                RAG: 0 at-risk → success, 1–2 → warning, 3+ → error.
                Count of projects where health IN ('red', 'amber') — PRD §2.10.
                Green positive state when 0 at-risk (PRD: "show green, not empty").
                Clicking scrolls to / highlights the Projects Requiring Attention widget. */}
            <KpiCard
              title="Projects at Risk"
              value={atRiskCount}
              icon={allHealthy ? CheckCircle2 : AlertTriangle}
              description={
                allHealthy
                  ? 'All projects healthy'
                  : `${redProjects} critical · ${amberProjects} amber`
              }
              accentVariant={
                allHealthy
                  ? 'success'
                  : atRiskCount >= 3
                    ? 'error'
                    : 'warning'
              }
            />

          </div>
        );
      }

      // ── Projects Requiring Attention — F9 ───────────────────────────────────
      // PRD §3, Insight 2 — compact smart table.
      // Columns: Project + Client | RAG | Root Cause Tag(s) | Budget Variance |
      //          Days Overdue | Assigned PM
      // Sort: red first → amber, then by over-budget severity.
      // Max 5 rows; "View all X" link if more.
      // Positive state: "All Projects Healthy" green card when 0 at-risk.
      // Row click → project detail page.
      case 'projects_requiring_attention': {
        // Root Cause Tag colour map
        const tagStyles: Record<string, string> = {
          'Over Budget': 'bg-destructive/10 text-destructive border-destructive/20',
          'Delayed': 'bg-warning/10 text-warning-700 dark:text-warning-400 border-warning/20',
          'Understaffed': 'bg-orange-50 text-orange-700 dark:text-orange-400 border-orange-200',
          'Unbilled WIP': 'bg-blue-50 text-blue-700 dark:text-blue-400 border-blue-200',
        };

        return (
          <div key="projects_requiring_attention" data-tour="dashboard-at-risk">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {atRiskTotal > 0 ? (
                      <AlertTriangle className="h-5 w-5 text-warning-600" />
                    ) : (
                      <CheckCircle2 className="h-5 w-5 text-success-600" />
                    )}
                    <CardTitle className="text-base">
                      Projects Requiring Attention
                    </CardTitle>
                    {atRiskTotal > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {atRiskTotal}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-muted-foreground"
                    onClick={() => navigateHook('/projects')}
                  >
                    View all projects
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Button>
                </div>
                <CardDescription>
                  {atRiskTotal > 0
                    ? 'Projects with active health issues — sorted by severity'
                    : 'All active projects are on track'}
                </CardDescription>
              </CardHeader>

              <CardContent className="p-0">
                {/* ── All Healthy state ── */}
                {atRiskTotal === 0 && (
                  <div className="px-6 pb-6 flex items-center gap-3 text-success-700 dark:text-success-400">
                    <CheckCircle2 className="h-8 w-8 flex-shrink-0 opacity-60" />
                    <div>
                      <p className="font-medium">All {isLiveMode ? (execKpis?.activeProjects ?? 0) : activeProjects.length} projects are healthy</p>
                      <p className="text-sm text-muted-foreground">No budget overruns, delays, or resourcing issues detected</p>
                    </div>
                  </div>
                )}

                {/* ── Table ── */}
                {atRiskTotal > 0 && (
                  <>
                    <Table>
                      <TableHeader divided>
                        <TableRow className="hover:bg-transparent">
                          <TableHead>Project / Client</TableHead>
                          <TableHead>RAG</TableHead>
                          <TableHead>Root Cause</TableHead>
                          <TableHead className="text-right">Budget Variance</TableHead>
                          <TableHead className="text-right">Days Overdue</TableHead>
                          <TableHead>PM</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {atRiskRowsVisible.map(({ project, rootCauseTags, pm, client, daysOverdue }) => (
                          <TableRow
                            key={project.id}
                            className="cursor-pointer"
                            onClick={() => handleNavigate(`projects/${project.id}`)}
                            onKeyDown={e => e.key === 'Enter' && handleNavigate(`projects/${project.id}`)}
                            tabIndex={0}
                            aria-label={`View details for ${project.name}`}
                          >
                            {/* Col 1 — Project name + client */}
                            <TableCell>
                              <p className="font-medium text-sm">{project.name}</p>
                              {client && (
                                <p className="text-xs text-muted-foreground">{client.name}</p>
                              )}
                            </TableCell>

                            {/* Col 2 — RAG badge */}
                            <TableCell>
                              <Badge
                                variant={healthToVariant(project.health as 'green' | 'amber' | 'red')}
                                className="gap-1.5 whitespace-nowrap"
                              >
                                <StatusIndicator
                                  variant={project.health === 'amber' ? 'warning' : 'error'}
                                  size="sm"
                                />
                                {project.health === 'amber' ? 'At Risk' : 'Critical'}
                              </Badge>
                            </TableCell>

                            {/* Col 3 — Root Cause Tag(s) */}
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {rootCauseTags.length > 0 ? (
                                  rootCauseTags.map(tag => (
                                    <span
                                      key={tag}
                                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${tagStyles[tag] ?? 'bg-muted text-muted-foreground border-border'}`}
                                    >
                                      {tag}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-muted-foreground italic">Review needed</span>
                                )}
                              </div>
                            </TableCell>

                            {/* Col 4 — Budget Variance */}
                            <TableCell className="text-right">
                              {project.costVariancePercent !== undefined ? (
                                <div>
                                  <span
                                    className={`text-sm font-medium ${
                                      project.costVariancePercent < 0
                                        ? 'text-destructive'
                                        : 'text-success-600'
                                    }`}
                                  >
                                    {project.costVariancePercent < 0 ? '' : '+'}
                                    {formatPercent(project.costVariancePercent, 1)}
                                  </span>
                                  {project.costVariance !== undefined && (
                                    <p className="text-xs text-muted-foreground">
                                      <SensitiveData>
                                        {project.costVariance < 0 ? '-' : '+'}
                                        {formatCurrency(Math.abs(project.costVariance))}
                                      </SensitiveData>
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>

                            {/* Col 5 — Days Overdue */}
                            <TableCell className="text-right">
                              {daysOverdue !== null ? (
                                <span
                                  className={`text-sm font-medium ${
                                    daysOverdue > 14 ? 'text-destructive' : 'text-warning-600'
                                  }`}
                                >
                                  {daysOverdue}d
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>

                            {/* Col 6 — Assigned PM */}
                            <TableCell>
                              {pm ? (
                                <span className="text-xs text-muted-foreground" title={pm.name}>
                                  {pm.name.split(' ')[0]} {pm.name.split(' ').slice(-1)[0]}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground italic">Unassigned</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* "View all" footer when more rows exist than are visible */}
                    {atRiskOverflow > 0 && (
                      <div className="px-6 py-3 border-t bg-muted/20 flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">
                          Showing {atRiskRowsVisible.length} of {atRiskTotal} at-risk projects
                        </p>
                        <Button
                          variant="link"
                          size="sm"
                          className="text-xs h-auto p-0"
                          onClick={() => navigateHook('/projects?health=at-risk')}
                        >
                          View all {atRiskTotal} at-risk projects
                          <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        );
      }

      // ── Upcoming Key Dates — F15 ────────────────────────────────────────────────
      // PRD §3, Insight 8 — Chronological prioritised list of events in the next
      // 30 days. Three row types: billing milestone due, project end approaching,
      // schedule overrun flagged. Urgency colour bands: Overdue / This Week / This Month.
      // Max 10 rows with overflow count; forecastEndDate column suppressed unless ≥1 overrun.
      case 'upcoming_key_dates':
        return (
          <Card key="upcoming_key_dates" className="transition-all hover:shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  Upcoming Key Dates
                </CardTitle>
                {/* Urgency summary chips */}
                <div className="flex items-center gap-1.5">
                  {(['overdue', 'this-week', 'this-month'] as KeyDateUrgency[]).map(u => {
                    const count = keyDateRows.filter(r => r.urgency === u).length;
                    if (count === 0) return null;
                    const cfg = urgencyConfig[u];
                    return (
                      <span
                        key={u}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.text}`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`} />
                        {count} {cfg.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              <CardDescription className="text-xs text-muted-foreground">
                Next 30 days — billing milestones, project ends &amp; schedule overruns
              </CardDescription>
            </CardHeader>

            <CardContent className="p-0">
              {keyDateRows.length === 0 ? (
                /* ── Empty state ───────────────────────────────────────────── */
                <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                  <AlertCircle className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-muted-foreground">No key dates in the next 30 days</p>
                  <p className="text-xs text-muted-foreground/70">All projects are on track with no upcoming billing or deadline events.</p>
                </div>
              ) : (
                <div>
                <Table>
                  <TableHeader divided>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Date</TableHead>
                      <TableHead>Project / Client</TableHead>
                      <TableHead className="text-right">Type</TableHead>
                      {hasAnyForecastEnd && <TableHead className="text-right">Forecast End</TableHead>}
                      <TableHead className="text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keyDateRowsVisible.map(row => {
                      const urgCfg  = urgencyConfig[row.urgency];
                      const typeCfg = keyDateTypeConfig[row.type];
                      const TypeIcon = typeCfg.icon;

                      const formattedDate = row.date.toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short',
                      });
                      const daysLabel = (() => {
                        const diffMs  = row.date.getTime() - todayStart.getTime();
                        const diffDay = Math.round(diffMs / (1000 * 60 * 60 * 24));
                        if (diffDay < 0)  return `${Math.abs(diffDay)}d overdue`;
                        if (diffDay === 0) return 'Today';
                        if (diffDay === 1) return 'Tomorrow';
                        return `in ${diffDay}d`;
                      })();

                      return (
                        <TableRow
                          key={row.id}
                          className="cursor-pointer"
                          onClick={() => handleNavigate(`projects/${row.projectId}`)}
                          tabIndex={0}
                          onKeyDown={e => e.key === 'Enter' && handleNavigate(`projects/${row.projectId}`)}
                        >
                          {/* Date */}
                          <TableCell>
                            <span className="text-sm font-medium tabular-nums block">{formattedDate}</span>
                            <span className={`text-[10px] font-medium ${urgCfg.text}`}>{daysLabel}</span>
                          </TableCell>

                          {/* Project / Client */}
                          <TableCell>
                            <p className="text-sm font-medium leading-tight">{row.projectName}</p>
                            <p className="text-xs text-muted-foreground">{row.clientName}</p>
                          </TableCell>

                          {/* Event type badge */}
                          <TableCell className="text-right">
                            <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${urgCfg.bg} ${urgCfg.text} border-transparent`}>
                              <TypeIcon className={`h-3 w-3 ${typeCfg.iconColor}`} />
                              {typeCfg.label}
                            </span>
                          </TableCell>

                          {/* Forecast end date (conditional column) */}
                          {hasAnyForecastEnd && (
                            <TableCell className="text-right">
                              {row.forecastEndDate ? (
                                <span className="text-xs font-medium text-orange-600">
                                  {row.forecastEndDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground/40">—</span>
                              )}
                            </TableCell>
                          )}

                          {/* Value */}
                          <TableCell className="text-right">
                            {row.amount != null && row.amount > 0 ? (
                              <SensitiveData>
                                <span className="text-sm font-semibold text-emerald-600">
                                  {formatCurrency(row.amount)}
                                </span>
                              </SensitiveData>
                            ) : (
                              <span className="text-xs text-muted-foreground/40">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                  {/* ── Overflow footer ─────────────────────────────────────── */}

                  {keyDateOverflow > 0 && (
                    <div className="border-t px-4 py-2.5">
                      <button
                        className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
                        onClick={() => handleNavigate('projects')}
                      >
                        <span>+{keyDateOverflow} more date{keyDateOverflow !== 1 ? 's' : ''}</span>
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* ── Legend strip ────────────────────────────────────────── */}
                  <div className="border-t bg-muted/20 px-4 py-2">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                      {(Object.entries(keyDateTypeConfig) as [KeyDateType, typeof keyDateTypeConfig[KeyDateType]][]).map(([type, cfg]) => {
                        const TypeIcon = cfg.icon;
                        return (
                          <span key={type} className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <TypeIcon className={`h-3 w-3 ${cfg.iconColor}`} />
                            {cfg.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );

      // ── Portfolio Profitability — F10 ────────────────────────────────────────
      // PRD §3, Insight 3 — Two-column combined card:
      //   Left  (35%) — 6-month rolling margin % line chart with optional dashed
      //                 target line and trend direction arrow.
      //   Right (65%) — Top 3 / Bottom 3 ranked table (all ranked if < 6 projects).
      //
      // Gross Margin % = (actualSales - actualCosts) / actualSales × 100 (§2.8).
      // Target line uses portfolioTargetMarginPercent from Settings (F18), falling
      // back to the firm-wide default when the company has not configured one.
      // Row click → project detail page.
      case 'portfolio_profitability': {
        // ── Helper: status label for each project row ──
        // getStatusLabel is destructured from useProjectStatuses() at component level

        // ── Helper: render inline trend chip ──
        // Retained for when backend month-over-month margin data is available.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        function TrendChip({ delta }: { delta: number }) {
          if (Math.abs(delta) < 0.1)
            return (
              <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground">
                <Minus className="h-3 w-3" />
                &mdash;
              </span>
            );
          return delta > 0 ? (
            <span className="inline-flex items-center gap-0.5 text-xs text-success-600">
              <ArrowUp className="h-3 w-3" />
              {Math.abs(delta).toFixed(1)}%
            </span>
          ) : (
            <span className="inline-flex items-center gap-0.5 text-xs text-destructive">
              <ArrowDown className="h-3 w-3" />
              {Math.abs(delta).toFixed(1)}%
            </span>
          );
        }

        // ── Margin % colour helper ──
        function marginColor(pct: number): string {
          if (pct < 0) return 'text-destructive font-semibold';
          if (pct < 10) return 'text-destructive';
          if (pct < 20) return 'text-warning-700 dark:text-warning-400';
          return 'text-success-700 dark:text-success-400';
        }

        // ── Ranked project table section ──
        function RankedSection({
          rows,
          label,
          labelClass,
          icon,
        }: {
          rows: typeof top3Projects;
          label: string;
          labelClass: string;
          icon: React.ReactNode;
        }) {
          if (rows.length === 0) return null;
          return (
            <div>
              {/* Section header */}
              <div className={`flex items-center gap-1.5 mb-2 px-4 text-xs font-semibold uppercase tracking-wide ${labelClass}`}>
                {icon}
                {label}
              </div>
              {/* Rows */}
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Project / Client</TableHead>
                    <TableHead className="text-right">Margin %</TableHead>
                    <TableHead className="text-right">Margin £</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(proj => (
                    <TableRow
                      key={proj.id}
                      className="cursor-pointer"
                      onClick={() => handleNavigate(`projects/${proj.id}`)}
                      tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && handleNavigate(`projects/${proj.id}`)}
                      aria-label={`View profitability for ${proj.name}`}
                    >
                      <TableCell>
                        <p className="font-medium">{proj.name}</p>
                        {proj.clientName && (
                          <p className="text-xs text-muted-foreground">{proj.clientName}</p>
                        )}
                      </TableCell>
                      <TableCell className={`text-right tabular-nums ${marginColor(proj.marginPct)}`}>
                        {proj.marginPct.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        <SensitiveData>
                          <span className={proj.marginGbp < 0 ? 'text-destructive' : ''}>
                            {formatCurrency(proj.marginGbp)}
                          </span>
                        </SensitiveData>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs whitespace-nowrap">
                          {getStatusLabel(proj.statusId) ?? formatStatus(String(proj.statusId))}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        }

        return (
          <Card key="portfolio_profitability">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  <CardTitle className="text-base">Portfolio Profitability</CardTitle>
                </div>
                <CardDescription className="text-xs">
                  Gross margin %. Active projects with actuals.
                </CardDescription>
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {projectsWithMargin.length === 0 ? (
                /* Empty state */
                <div className="px-6 pb-6 flex items-center gap-3 text-muted-foreground">
                  <Target className="h-8 w-8 opacity-30" />
                  <div>
                    <p className="font-medium">No profitability data yet</p>
                    <p className="text-sm">Projects need actual sales recorded before margin can be calculated.</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col lg:flex-row">

                  {/* ── Left column — Margin distribution + KPI summary (35%) ── */}
                  <div className="lg:w-[35%] p-4 border-b lg:border-b-0 lg:border-r border-border flex flex-col gap-4">

                    {/* KPI summary row */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg bg-muted/40 px-3 py-2">
                        <p className="text-xs text-muted-foreground">Portfolio Margin</p>
                        <p className={`font-semibold text-lg leading-tight ${marginColor(portfolioMargin)}`}>
                          <SensitiveData>{formatPercent(portfolioMargin, 1)}</SensitiveData>
                        </p>
                        <p className={`text-xs mt-0.5 ${portfolioMargin >= portfolioTargetMarginPct ? 'text-success-600 dark:text-success-400' : 'text-destructive'}`}>
                          {portfolioMargin >= portfolioTargetMarginPct ? '↑' : '↓'} target {portfolioTargetMarginPct}%
                        </p>
                      </div>
                      <div className="rounded-lg bg-muted/40 px-3 py-2">
                        <p className="text-xs text-muted-foreground">Portfolio GP</p>
                        <p className={`font-semibold text-lg leading-tight ${totalProfit < 0 ? 'text-destructive' : 'text-success-700 dark:text-success-400'}`}>
                          <SensitiveData>{formatCurrency(totalProfit)}</SensitiveData>
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {projectsWithMargin.length} project{projectsWithMargin.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>

                    {/* Margin band distribution */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                        Margin Distribution
                      </p>
                      <div className="space-y-2">
                        {marginBands.map(band => {
                          const pct = projectsWithMargin.length > 0
                            ? (band.projects.length / projectsWithMargin.length) * 100
                            : 0;
                          return (
                            <div key={band.label}>
                              <div className="flex items-center justify-between text-xs mb-0.5">
                                <span className="flex items-center gap-1.5">
                                  <span className={`inline-block w-2 h-2 rounded-sm ${band.color}`} />
                                  <span className="font-medium">{band.label}</span>
                                  <span className="text-muted-foreground">{band.range}</span>
                                </span>
                                <span className={`font-semibold ${band.projects.length > 0 ? band.textColor : 'text-muted-foreground'}`}>
                                  {band.projects.length}
                                </span>
                              </div>
                              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${band.color}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Loss-making callout — only shown if any exist */}
                    {marginBands[0].projects.length > 0 && (
                      <div className="rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2 text-xs">
                        <p className="font-medium text-destructive mb-0.5">
                          {marginBands[0].projects.length} loss-making project{marginBands[0].projects.length !== 1 ? 's' : ''}
                        </p>
                        <p className="text-muted-foreground truncate">
                          {marginBands[0].projects.slice(0, 2).map(p => p.name).join(', ')}
                          {marginBands[0].projects.length > 2 && ` +${marginBands[0].projects.length - 2} more`}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ── Right column — Top 3 / Bottom 3 ranked table (65%) ── */}
                  <div className="lg:w-[65%] pt-4 space-y-4">

                    {portfolioProfitabilityAllProjects ? (
                      /* All projects ranked (< 6) */
                      <RankedSection
                        rows={top3Projects}
                        label="All Projects — Ranked by Margin"
                        labelClass="text-muted-foreground"
                        icon={<Target className="h-3.5 w-3.5" />}
                      />
                    ) : (
                      /* Top 3 + Bottom 3 split */
                      <>
                        <RankedSection
                          rows={top3Projects}
                          label="Top 3 — Highest Margin"
                          labelClass="text-success-700 dark:text-success-400"
                          icon={<TrendingUp className="h-3.5 w-3.5" />}
                        />
                        <RankedSection
                          rows={bottom3Projects}
                          label="Bottom 3 — Lowest Margin"
                          labelClass="text-destructive"
                          icon={<TrendingDown className="h-3.5 w-3.5" />}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      }

      // ── Revenue vs Cost Burn ──────────────────────────────────────────────────
      case 'revenue_vs_cost_burn': {
        const activeData = rcbView === 'month' ? byMonthData : byProjectData;
        const breakEvenValue = rcbView === 'month' ? breakEvenByMonth : breakEvenByProject;
        const xKey = rcbView === 'month' ? 'month' : 'name';
        const isEmpty = activeData.length === 0;
        return (
          <Card key="revenue_vs_cost_burn">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-primary" />
                    Sales vs Cost
                  </CardTitle>
                  <CardDescription className="mt-1">
                    {rcbView === 'month'
                      ? 'Last 6 months Sales vs Cost'
                      : `Top ${byProjectTop10.length} active projects by contract value${byProjectHasMore ? ' — View all in reports' : ''}`}
                  </CardDescription>
                </div>
                {/* By Month / By Project toggle */}
                <div className="flex rounded-md border border-border overflow-hidden shrink-0">
                  <button
                    onClick={() => setRcbView('month')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      rcbView === 'month'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    By Month
                  </button>
                  <button
                    onClick={() => setRcbView('project')}
                    className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-border ${
                      rcbView === 'project'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    By Project
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isEmpty ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
                  <TrendingUp className="h-10 w-10 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">
                    No actuals data available yet.
                  </p>
                </div>
              ) : (
                <>
                  {/* Colour-code legend */}
                  <div className="flex flex-wrap gap-4 mb-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-sm bg-[var(--muted-foreground)] opacity-60" />
                      Planned Cost
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-sm bg-[var(--primary)]" />
                      Actual Cost
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: C_WARNING }} />
                      5–15% over budget
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: C_DANGER }} />
                      &gt;15% over budget
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: C_SUCCESS }} />
                      Actual Sales
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart
                      data={activeData}
                      margin={{ top: 8, right: 16, left: 0, bottom: rcbView === 'project' ? 60 : 8 }}
                      barCategoryGap="25%"
                      barGap={2}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                      <XAxis
                        dataKey={xKey}
                        tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                        angle={rcbView === 'project' ? -40 : 0}
                        textAnchor={rcbView === 'project' ? 'end' : 'middle'}
                        height={rcbView === 'project' ? 70 : 30}
                        interval={0}
                      />
                      <YAxis
                        tick={{ fill: 'var(--muted-foreground)', fontSize: 11 }}
                        tickFormatter={v => `£${(v / 1000).toFixed(0)}k`}
                        width={56}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--card)',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          fontSize: '12px',
                        }}
                        formatter={(value, name, props) => {
                          const v = Number(value);
                          const formattedVal = formatCurrency(v);
                          if (name === 'Actual Cost') {
                            const vPct = props?.payload?.variancePct ?? 0;
                            const sign = vPct >= 0 ? '+' : '';
                            return [`${formattedVal} (${sign}${vPct.toFixed(1)}% vs plan)`, name];
                          }
                          return [formattedVal, String(name)];
                        }}
                      />
                      {/* Break-even reference line */}
                      {breakEvenValue > 0 && (
                        <ReferenceLine
                          y={breakEvenValue}
                          stroke="var(--primary)"
                          strokeDasharray="6 3"
                          strokeWidth={1.5}
                          label={{
                            value: 'Break-even',
                            position: 'insideTopRight',
                            fill: 'var(--primary)',
                            fontSize: 10,
                          }}
                        />
                      )}
                      {/* Planned Cost — grey fill */}
                      <Bar dataKey="plannedCost" name="Planned Cost" fill="var(--muted-foreground)" opacity={0.4} radius={[2, 2, 0, 0]} />
                      {/* Actual Cost — colour-coded per bar via Cell */}
                      <Bar dataKey="actualCost" name="Actual Cost" radius={[2, 2, 0, 0]}>
                        {activeData.map((entry, index) => (
                          <Cell key={`ac-${index}`} fill={entry.actualCostColor} />
                        ))}
                      </Bar>
                      {/* Actual Revenue — green */}
                      <Bar dataKey="actualRevenue" name="Actual Sales" fill={C_SUCCESS} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  {/* By Project: "View all" link when there are more than 10 */}
                  {rcbView === 'project' && byProjectHasMore && (
                    <div className="mt-3 text-right">
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs text-primary"
                        onClick={() => handleNavigate('profitability-report')}
                      >
                        View all {byProjectSorted.length} projects →
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      }

      // ── DEAD CODE — kept only so old revenueVsCostData derivation compiles ───
      // This block is never reached; revenueVsCostData is retained above for
      // potential future use by other widgets.
      case '__revenue_vs_cost_burn_legacy__':
        return (
          <div key="__legacy__">
            {/* Revenue vs Cost bar chart */}
            <Card className="md:col-span-2 lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Sales vs Cost
                </CardTitle>
                <CardDescription>Top 5 projects by sales</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={revenueVsCostData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: 'var(--muted-foreground)' }}
                      fontSize={10}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{ fill: 'var(--muted-foreground)' }}
                      fontSize={12}
                      tickFormatter={v => `£${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'var(--card)',
                        border: '1px solid var(--border)',
                        borderRadius: '6px',
                      }}
                      formatter={v => formatCurrency(Number(v))}
                    />
                    <Legend />
                    <Bar dataKey="revenue" fill={C_SUCCESS} name="Revenue" />
                    <Bar dataKey="cost" fill={C_WARNING} name="Cost" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        );

      // ── Billing & Cash Flow Health — F13 ────────────────────────────────────
      case 'billing_cash_flow_health': {
        const healthCfg = cashFlowHealthConfig[cashFlowHealthScore];
        const HealthIcon = healthCfg.icon;

        // Invoice pipeline table rows
        const pipelineRows = [
          {
            label: 'Due this month',
            fp: fpThisMonth,
            fpExact: true,
            tam: tamThisMonth,
            tamExact: !tamThisMonthIsEstimate,
          },
          {
            label: 'Due next month',
            fp: fpNextMonth,
            fpExact: true,
            tam: tamNextMonth,
            tamExact: !tamNextMonthIsEstimate,
          },
          {
            label: 'Overdue',
            fp: fpOverdue,
            fpExact: true,
            tam: tamOverdue,
            tamExact: !tamOverdueIsEstimate,
            isOverdue: true,
          },
        ];

        const fpTotal  = fpThisMonth + fpNextMonth + fpOverdue;
        const tamTotal = tamThisMonth + tamNextMonth + tamOverdue;
        const tamTotalIsEstimate = tamThisMonthIsEstimate || tamNextMonthIsEstimate || tamOverdueIsEstimate;
        const fixedPriceWithoutSchedule = livePipeline?.fixedPriceProjectsWithoutSchedule ?? 0;

        return (
          <Card key="billing_cash_flow_health" className="col-span-full lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                  Billing &amp; Cash Flow Health
                </CardTitle>
                {/* Component C — Cash Flow Health Score badge */}
                <div className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${healthCfg.bg} ${healthCfg.text} ${healthCfg.border}`}>
                  <HealthIcon className="h-3.5 w-3.5" />
                  {healthCfg.label}
                </div>
              </div>
              <CardDescription className="text-xs">
                Portfolio WIP aging · Invoice pipeline · Cash flow health
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4 pt-0">

              {/* ── Component A — WIP Summary ──────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    WIP Aging
                  </span>
                  <button
                    onClick={() => handleNavigate('reports/wip')}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    <SensitiveData>
                      <span className="font-semibold text-foreground">{formatCurrency(portfolioWipTotal)}</span>
                    </SensitiveData>
                    <span className="text-muted-foreground">total WIP</span>
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>

                {wipView.positiveWipCount === 0 ? (
                  <div className="flex items-center gap-2 rounded-md bg-muted/40 px-3 py-2.5 text-xs text-muted-foreground">
                    <Banknote className="h-4 w-4 shrink-0" />
                    No WIP — all work is billed up to date
                  </div>
                ) : (
                  <AgedWipStrip
                    wipItems={[]}
                    bandTotals={portfolioWipBands}
                    writtenOffIds={new Set()}
                    activeBand={wipActiveBand}
                    onBandClick={(band) => {
                      setWipActiveBand(band);
                      if (band) {
                        navigateHook(`/reports/wip?agingBand=${encodeURIComponent(band)}`);
                      }
                    }}
                  />
                )}
              </div>

              <div className="border-t" />

              {/* ── Component B — Invoice Pipeline ─────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Invoice Pipeline
                  </span>
                  <button
                    onClick={() => handleNavigate('reports/wip')}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    View invoicing
                    <ArrowRight className="h-3 w-3" />
                  </button>
                </div>

                <div className="overflow-hidden rounded-md border text-xs">
                  {/* Header row */}
                  <div className="grid grid-cols-3 gap-0 border-b bg-muted/50">
                    <div className="px-3 py-1.5 font-medium text-muted-foreground"></div>
                    <div className="border-l px-3 py-1.5 font-medium text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        Fixed Price
                      </div>
                    </div>
                    <div className="border-l px-3 py-1.5 font-medium text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Activity className="h-3 w-3" />
                        T&amp;M / Retainer
                      </div>
                    </div>
                  </div>

                  {/* Data rows */}
                  {pipelineRows.map((row, idx) => (
                    <div
                      key={row.label}
                      className={`grid grid-cols-3 gap-0 ${idx < pipelineRows.length - 1 ? 'border-b' : ''} ${row.isOverdue ? 'bg-destructive/5' : ''}`}
                    >
                      <div className={`px-3 py-2 font-medium ${row.isOverdue ? 'text-destructive' : 'text-foreground'}`}>
                        {row.label}
                      </div>
                      <div className="border-l px-3 py-2 tabular-nums">
                        <SensitiveData>
                          {row.fp > 0 ? (
                            <span className={row.isOverdue ? 'font-semibold text-destructive' : 'text-foreground'}>
                              {formatCurrency(row.fp)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </SensitiveData>
                      </div>
                      <div className="border-l px-3 py-2 tabular-nums">
                        <SensitiveData>
                          {row.tam > 0 ? (
                            <span className={row.isOverdue ? 'font-semibold text-destructive' : 'text-foreground'}>
                              {formatCurrency(row.tam)}
                              {!row.tamExact && (
                                <span className="ml-1 text-muted-foreground font-normal">Est.</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </SensitiveData>
                      </div>
                    </div>
                  ))}

                  {/* Totals row */}
                  <div className="grid grid-cols-3 gap-0 border-t bg-muted/30">
                    <div className="px-3 py-2 font-semibold text-foreground">Total</div>
                    <div className="border-l px-3 py-2 tabular-nums font-semibold">
                      <SensitiveData>
                        {fpTotal > 0 ? formatCurrency(fpTotal) : <span className="text-muted-foreground font-normal">—</span>}
                      </SensitiveData>
                    </div>
                    <div className="border-l px-3 py-2 tabular-nums font-semibold">
                      <SensitiveData>
                        {tamTotal > 0 ? (
                          <>
                            {formatCurrency(tamTotal)}
                            {tamTotalIsEstimate && (
                              <span className="ml-1 text-xs text-muted-foreground font-normal">Est.</span>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground font-normal">—</span>
                        )}
                      </SensitiveData>
                    </div>
                  </div>
                </div>

                {fixedPriceWithoutSchedule > 0 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {fixedPriceWithoutSchedule} fixed-price {fixedPriceWithoutSchedule === 1 ? 'project has' : 'projects have'} no scheduled billing milestones and {fixedPriceWithoutSchedule === 1 ? 'is' : 'are'} not included.
                  </p>
                )}

                {/* Health score context sentence */}
                <p className={`mt-2 text-xs ${healthCfg.text}`}>
                  {cashFlowHealthScore === 'healthy' && '✓ Cash flow looks healthy — WIP is current and no overdue invoices.'}
                  {cashFlowHealthScore === 'watch' && '⚠ Some ageing WIP or minor overdue invoices detected. Monitor closely.'}
                  {cashFlowHealthScore === 'at-risk' && '✖ Significant overdue invoices or 60+ day WIP. Action required.'}
                </p>
              </div>

            </CardContent>
          </Card>
        );
      }

      // ── Client Concentration Risk — F14 ──────────────────────────────────────
      // PRD §3 Insight 7 — horizontal bar chart of RCV per client,
      // vertical threshold line, health badge, summary sentence.
      case 'client_concentration_risk': {
        const healthCfg = concentrationHealthConfig[concentrationHealth];
        const HealthIcon =
          concentrationHealth === 'well-diversified' ? ShieldCheck
          : concentrationHealth === 'moderate'       ? ShieldAlert
          : ShieldX;
        const thresholdPct = concentrationThresholdPercent;

        // Empty state — no active projects
        if (clientRcvRows.length === 0) {
          return (
            <Card key="client_concentration_risk" className="col-span-full lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <PieChartIcon className="h-4 w-4 text-muted-foreground" />
                  Client Concentration Risk
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center py-10 gap-3 text-center">
                <div className="rounded-full bg-muted p-4">
                  <Building2 className="h-8 w-8 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">No active projects to analyse</p>
              </CardContent>
            </Card>
          );
        }

        return (
          <Card key="client_concentration_risk" className="col-span-full lg:col-span-1">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <PieChartIcon className="h-4 w-4 text-muted-foreground" />
                  Client Concentration Risk
                </CardTitle>
                {/* Health badge */}
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${healthCfg.bg} ${healthCfg.text} ${healthCfg.border}`}>
                  <HealthIcon className="h-3 w-3" />
                  {healthCfg.label}
                </span>
              </div>
              {/* Summary sentence */}
              {sortedClientRcv.length >= 3 ? (
                <CardDescription className="mt-1">
                  Your top {Math.min(3, sortedClientRcv.length)} clients represent{' '}
                  <span className="font-semibold text-foreground">{formatPercent(top3Pct * 100, 0)}</span>
                  {' '}of active portfolio remaining contract value
                </CardDescription>
              ) : (
                <CardDescription className="mt-1">
                  {sortedClientRcv.length} active client{sortedClientRcv.length !== 1 ? 's' : ''} — add more projects to see concentration analysis
                </CardDescription>
              )}
            </CardHeader>

            <CardContent className="space-y-1 pt-0">
              {/* Column headers */}
              <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground font-medium">
                <span>Client</span>
                <div className="flex items-center gap-6">
                  <span>RCV</span>
                  <span className="w-16 text-right">Share</span>
                </div>
              </div>

              {/* Bars */}
              {clientRcvRows.map((row, idx) => {
                const barPct     = row.pct * 100;                        // % of total portfolio
                const overThresh = !row.isOther && row.pct > (concentrationThresholdPct);
                return (
                  <div
                    key={row.clientName}
                    className={`group relative rounded-md px-2 py-1.5 transition-colors ${
                      row.isOther ? '' : 'cursor-pointer hover:bg-muted/60'
                    }`}
                    onClick={() => {
                      if (!row.isOther) {
                        handleNavigate(`projects?client=${encodeURIComponent(row.clientName)}`);
                      }
                    }}
                  >
                    {/* Client name + project count */}
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {!row.isOther && <Building2 className="h-3 w-3 shrink-0 text-muted-foreground" />}
                        <span className={`text-xs font-medium truncate ${row.isOther ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                          {row.clientName}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          ({row.projectCount} {row.projectCount === 1 ? 'project' : 'projects'})
                        </span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="text-xs text-muted-foreground">{formatCurrency(row.rcv)}</span>
                        <span className={`text-xs font-semibold w-12 text-right ${overThresh ? 'text-destructive' : 'text-foreground'}`}>
                          {formatPercent(barPct, 1)}
                        </span>
                      </div>
                    </div>

                    {/* Horizontal bar track */}
                    <div className="relative h-2 w-full rounded-full bg-muted overflow-visible">
                      {/* Fill bar */}
                      <div
                        className={`h-2 rounded-full transition-all ${
                          row.isOther
                            ? 'bg-muted-foreground/40'
                            : overThresh
                              ? 'bg-destructive/70'
                              : idx === 0
                                ? 'bg-primary'
                                : 'bg-primary/60'
                        }`}
                        style={{ width: `${Math.min(barPct, 100)}%` }}
                      />
                      {/* Threshold marker line — only on first bar for clarity */}
                      {idx === 0 && totalPortfolioRcv > 0 && (
                        <div
                          className="absolute top-[-4px] bottom-[-4px] w-px bg-foreground/40 z-10"
                          style={{ left: `${Math.min(thresholdPct, 100)}%` }}
                          title={`Threshold: ${thresholdPct}%`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Threshold annotation */}
              {totalPortfolioRcv > 0 && (
                <div className="flex items-center gap-1.5 pt-2 text-xs text-muted-foreground">
                  <div className="h-3 w-px bg-foreground/40 shrink-0" />
                  <span>Concentration threshold: {thresholdPct}% per client</span>
                  {concentrationHealth !== 'well-diversified' && hasEnoughClients && (
                    <span className={`ml-1 font-medium ${healthCfg.text}`}>
                      — {healthCfg.label}
                    </span>
                  )}
                </div>
              )}

              {/* Context sentence for at-risk states */}
              {concentrationHealth === 'critical' && hasEnoughClients && (
                <p className="text-xs text-destructive pt-1">
                  ⚠ {sortedClientRcv[0]?.clientName} represents {formatPercent(topClientPct * 100, 0)} of your portfolio — critical single-client dependency.
                </p>
              )}
              {concentrationHealth === 'high' && hasEnoughClients && (
                <p className="text-xs text-orange-600 pt-1">
                  {sortedClientRcv[0]?.clientName} represents {formatPercent(topClientPct * 100, 0)} of your portfolio — consider diversifying.
                </p>
              )}
            </CardContent>
          </Card>
        );
      }

      // ── Utilisation Snapshot — F12 ────────────────────────────────────────────
      case 'utilisation_snapshot': {
        // No integration → connect prompt
        if (!hasAnyUtilisationIntegration || utilisationEmployeeCount === 0) {
          return (
            <Card key="utilisation_snapshot" className="col-span-full lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Gauge className="h-4 w-4 text-muted-foreground" />
                  Utilisation Snapshot
                </CardTitle>
                <CardDescription>Connect a planning tool or timesheet system to see utilisation data</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center justify-center py-10 gap-4">
                <div className="rounded-full bg-muted p-4">
                  <Plug className="h-8 w-8 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium">No utilisation data available</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Connect Access Planner, Access People Planner, or a timesheet system in Settings → Integrations
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleNavigate('settings/integrations')}
                  className="mt-2"
                >
                  <Plug className="h-3 w-3 mr-2" />
                  Connect Integration
                </Button>
              </CardContent>
            </Card>
          );
        }

        // Status colour helpers
        const statusColor = (s: 'under' | 'target' | 'over') =>
          s === 'over' ? C_DANGER : s === 'target' ? C_SUCCESS : C_WARNING;
        const statusLabel = (s: 'under' | 'target' | 'over') =>
          s === 'over' ? 'Over' : s === 'target' ? 'On Target' : 'Under';

        // Gauge segments: under 0–65 (amber), healthy 65–85 (green), over 85–100 (red)
        // We represent the gauge as a semi-circle using SVG arcs
        const gaugeRadius = 60;
        const gaugeCx = 80;
        const gaugeCy = 75;
        const gaugeStroke = 14;

        // Convert % to SVG arc coords on a semi-circle (180° sweep, left to right)
        const pctToAngle = (pct: number) => (pct / 100) * 180 - 90; // -90° to +90°
        const polarToXY = (angleDeg: number, r: number) => ({
          x: gaugeCx + r * Math.cos((angleDeg * Math.PI) / 180),
          y: gaugeCy + r * Math.sin((angleDeg * Math.PI) / 180),
        });

        const arcPath = (startPct: number, endPct: number) => {
          const s = polarToXY(pctToAngle(startPct), gaugeRadius);
          const e = polarToXY(pctToAngle(endPct), gaugeRadius);
          const largeArc = endPct - startPct > 50 ? 1 : 0;
          return `M ${s.x} ${s.y} A ${gaugeRadius} ${gaugeRadius} 0 ${largeArc} 1 ${e.x} ${e.y}`;
        };

        // Needle pointing to blendedUtilisationPct
        const needleAngle = pctToAngle(Math.min(Math.max(blendedUtilisationPct, 0), 100));
        const needleTip = polarToXY(needleAngle, gaugeRadius - 6);
        const needleBase1 = polarToXY(needleAngle + 90, 5);
        const needleBase2 = polarToXY(needleAngle - 90, 5);

        // Target needle
        const targetNeedleAngle = pctToAngle(Math.min(Math.max(targetUtilisationPct, 0), 100));
        const targetNeedleTip = polarToXY(targetNeedleAngle, gaugeRadius + 4);

        return (
          <Card key="utilisation_snapshot" className="col-span-full lg:col-span-1">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base font-semibold">
                  <Gauge className="h-4 w-4 text-muted-foreground" />
                  {utilisationWidgetTitle}
                </CardTitle>
                {/* Trend chip */}
                <div className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  utilisationTrendDelta >= 0
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                    : 'bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-400'
                }`}>
                  {utilisationTrendDelta >= 0
                    ? <ArrowUp className="h-3 w-3" />
                    : <ArrowDown className="h-3 w-3" />}
                  {utilisationTrendDelta >= 0 ? '+' : ''}{formatPercent(Math.abs(utilisationTrendDelta), 1)} vs last month
                </div>
              </div>
              <CardDescription>{utilisationWidgetLabel}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">

                {/* ── Component A: Utilisation Gauge ────────────────────────── */}
                <div
                  className="flex flex-col items-center cursor-pointer shrink-0"
                  onClick={() => handleNavigate('utilisation')}
                  role="button"
                  aria-label="View utilisation detail"
                >
                  <svg width="160" height="90" viewBox="0 0 160 90" className="overflow-visible">
                    {/* Track arcs — colour zones */}
                    {/* Under-utilised: 0–65% → amber */}
                    <path
                      d={arcPath(0, 65)}
                      fill="none"
                      stroke={C_WARNING}
                      strokeWidth={gaugeStroke}
                      strokeLinecap="butt"
                      opacity={0.25}
                    />
                    {/* Healthy: 65–85% → green */}
                    <path
                      d={arcPath(65, 85)}
                      fill="none"
                      stroke={C_SUCCESS}
                      strokeWidth={gaugeStroke}
                      strokeLinecap="butt"
                      opacity={0.25}
                    />
                    {/* Over-utilised: 85–100% → red */}
                    <path
                      d={arcPath(85, 100)}
                      fill="none"
                      stroke={C_DANGER}
                      strokeWidth={gaugeStroke}
                      strokeLinecap="butt"
                      opacity={0.25}
                    />

                    {/* Filled arc from 0 to current value */}
                    <path
                      d={arcPath(0, Math.min(Math.max(blendedUtilisationPct, 0.5), 100))}
                      fill="none"
                      stroke={gaugeColor}
                      strokeWidth={gaugeStroke}
                      strokeLinecap="butt"
                    />

                    {/* Target line tick (dashed) */}
                    <line
                      x1={polarToXY(targetNeedleAngle, gaugeRadius - gaugeStroke / 2 - 2).x}
                      y1={polarToXY(targetNeedleAngle, gaugeRadius - gaugeStroke / 2 - 2).y}
                      x2={targetNeedleTip.x}
                      y2={targetNeedleTip.y}
                      stroke="var(--primary)"
                      strokeWidth={2}
                      strokeDasharray="3 2"
                    />

                    {/* Needle */}
                    <polygon
                      points={`${needleTip.x},${needleTip.y} ${needleBase1.x},${needleBase1.y} ${needleBase2.x},${needleBase2.y}`}
                      fill={gaugeColor}
                    />
                    {/* Needle pivot */}
                    <circle cx={gaugeCx} cy={gaugeCy} r={5} fill={gaugeColor} />

                    {/* Zone labels */}
                    <text x={10} y={88} fontSize={8} fill={C_WARNING} textAnchor="start">0%</text>
                    <text x={80} y={14} fontSize={8} fill={C_SUCCESS} textAnchor="middle">65–85%</text>
                    <text x={152} y={88} fontSize={8} fill={C_DANGER} textAnchor="end">100%</text>
                  </svg>

                  {/* Central % display */}
                  <div className="text-center -mt-2">
                    <span className="text-2xl font-bold" style={{ color: gaugeColor }}>
                      {blendedUtilisationPct}%
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">Blended utilisation</p>
                    <p className="text-xs text-muted-foreground">
                      Target: <span className="font-medium text-primary">{targetUtilisationPct}%</span>
                    </p>
                    {benchCount > 0 && (
                      <Badge variant="secondary" className="mt-1 text-xs">
                        {benchCount} on bench
                      </Badge>
                    )}
                  </div>
                </div>

                {/* ── Component B: Department Bars ─────────────────────────── */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
                    By Department
                  </p>
                  <div className="space-y-2">
                    {deptUtilisation.length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No department data available</p>
                    ) : (
                      deptUtilisation.map(dept => (
                        <div
                          key={dept.department}
                          className="cursor-pointer group"
                          onClick={() => handleNavigate('utilisation')}
                          role="button"
                          aria-label={`${dept.department} utilisation: ${dept.utilisationPercent}%`}
                        >
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-xs font-medium truncate max-w-[120px] group-hover:underline">
                              {dept.department}
                            </span>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className="text-xs text-muted-foreground">
                                {dept.headcount} staff
                              </span>
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 h-4"
                                style={{
                                  borderColor: statusColor(dept.status),
                                  color: statusColor(dept.status),
                                }}
                              >
                                {statusLabel(dept.status)}
                              </Badge>
                              <span className="text-xs font-semibold w-8 text-right" style={{ color: statusColor(dept.status) }}>
                                {dept.utilisationPercent}%
                              </span>
                            </div>
                          </div>
                          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${Math.min(dept.utilisationPercent, 100)}%`,
                                backgroundColor: statusColor(dept.status),
                              }}
                            />
                          </div>
                          {/* Target line marker */}
                          <div className="relative h-0">
                            <div
                              className="absolute -top-1.5 h-4 w-px bg-primary opacity-60"
                              style={{ left: `${Math.min(targetUtilisationPct, 100)}%` }}
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* ── Component C: Trend sentence ────────────────────────────── */}
              <div className="mt-3 pt-3 border-t flex items-center gap-2 text-xs text-muted-foreground">
                <Activity className="h-3.5 w-3.5 shrink-0" />
                {benchCount > 0
                  ? <span><strong className="text-foreground">{benchCount} staff</strong> currently under target utilisation</span>
                  : utilisationTrendDelta >= 0
                    ? <span>Utilisation is <strong className="text-emerald-600 dark:text-emerald-400">up {formatPercent(utilisationTrendDelta, 1)}</strong> vs last month</span>
                    : <span>Utilisation is <strong className="text-error-600 dark:text-error-400">down {formatPercent(Math.abs(utilisationTrendDelta), 1)}</strong> vs last month</span>
                }
              </div>
            </CardContent>
          </Card>
        );
      }

      // ── Headcount & Delivery Capacity — F16 ──────────────────────────────────
      // PRD §3, Insight 9 — 3 KPI tiles (top) + department capacity bars (bottom).
      // Mode-aware (planning tool waterfall, same as F12 Utilisation Snapshot).
      // "Available Now" tile click → /utilisation filtered to under-utilised.
      // Department bars suppressed if < 3 departments (show tiles only).
      case 'headcount_delivery_capacity': {
        const isNoIntegration = utilisationMode === 'none';
        const widgetTitle =
          utilisationMode === 'timesheet'
            ? 'Headcount & Billable Capacity'
            : utilisationMode === 'planner'
              ? 'Headcount & Delivery Capacity'
              : 'Headcount Overview';

        // No-integration state: show connect prompt
        if (isNoIntegration && utilisationEmployeeCount === 0) {
          return (
            <Card key="headcount_delivery_capacity">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {widgetTitle}
                  </CardTitle>
                </div>
                <CardDescription>Total headcount and capacity by department</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-8 text-center gap-3">
                  <div className="rounded-full bg-muted p-3">
                    <Plug className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">No resource data available</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Connect your planning tool or timesheet system to unlock capacity insights
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleNavigate('settings-integrations')}
                  >
                    Connect integration
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        }

        return (
          <Card key="headcount_delivery_capacity">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  {widgetTitle}
                </CardTitle>
                {/* Integration badge showing data source mode */}
                <span className="text-xs text-muted-foreground bg-muted rounded px-2 py-0.5">
                  {utilisationMode === 'planner'
                    ? 'Planning tool'
                    : utilisationMode === 'timesheet'
                      ? 'Timesheets'
                      : 'Demo data'}
                </span>
              </div>
              <CardDescription>
                {utilisationMode === 'planner'
                  ? 'Planned allocation next 4 weeks'
                  : utilisationMode === 'timesheet'
                    ? 'Billable hours last 30 days'
                    : 'Headcount and capacity by department'}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-5">
              {/* ── Component A: 3 KPI Tiles ──────────────────────────── */}
              <div className="grid grid-cols-3 gap-3">
                {/* Tile 1 — Total Headcount */}
                <CompactKpiCard
                  title="Total Headcount"
                  value={totalHeadcount}
                  icon={Users}
                  description="Available staff"
                  accentVariant="info"
                  className="shadow-none"
                />

                {/* Tile 2 — Currently Allocated */}
                <CompactKpiCard
                  title="Allocated"
                  value={currentlyAllocatedCount}
                  icon={UserCheck}
                  description={
                    totalHeadcount > 0
                      ? `${Math.round((currentlyAllocatedCount / totalHeadcount) * 100)}% of headcount`
                      : 'No staff records'
                  }
                  accentVariant="info"
                  className="shadow-none"
                />

                {/* Tile 3 — Available Now (clickable → /utilisation) */}
                <div
                  onClick={() => handleNavigate('utilisation')}
                  className="cursor-pointer"
                  title="View under-utilised staff"
                >
                  <CompactKpiCard
                    title="Available Now"
                    value={availableNowCount}
                    icon={UserMinus}
                    description={
                      availableNowCount === 0
                        ? 'Fully booked'
                        : availableNowCount === 1
                          ? '1 staff under-utilised'
                          : `${availableNowCount} staff under-utilised`
                    }
                    accentVariant={
                      availableNowCount === 0
                        ? 'success'
                        : availableNowCount <= 3
                          ? 'warning'
                          : 'error'
                    }
                    className="shadow-none h-full"
                  />
                </div>
              </div>

              {/* ── Component B: Department Capacity Bars ─────────────── */}
              {/* Suppressed if < 3 departments (show tiles only) */}
              {showDeptBars && (
                <div className="space-y-2.5 pt-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Capacity by Department
                  </p>

                  {headcountDeptBars.map(dept => {
                    const colors = deptCapacityColorMap[dept.status];
                    return (
                      <div key={dept.department} className="space-y-1">
                        {/* Department label row */}
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">{dept.department}</span>
                            <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${colors.badge}`}>
                              {dept.status === 'under'
                                ? 'Under'
                                : dept.status === 'over'
                                  ? 'Over'
                                  : 'On Target'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className={`font-semibold ${colors.text}`}>
                              {dept.utilisationPercent}%
                            </span>
                            <span className="text-muted-foreground">
                              {dept.headcount} {dept.headcount === 1 ? 'person' : 'people'}
                            </span>
                          </div>
                        </div>

                        {/* Capacity bar */}
                        <div className="relative h-2 rounded-full bg-muted overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${colors.bar}`}
                            style={{ width: `${Math.min(dept.utilisationPercent, 100)}%` }}
                          />
                          {/* Target line at configured % */}
                          <div
                            className="absolute top-0 h-full w-px bg-primary/60"
                            style={{ left: `${Math.min(targetUtilisationPct, 100)}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}

                  {/* Footer: navigate to full utilisation page */}
                  <div className="flex justify-end pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleNavigate('utilisation')}
                    >
                      View full utilisation
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              )}

              {/* When < 3 departments, show a simple summary sentence instead */}
              {!showDeptBars && utilisationEmployeeCount > 0 && (
                <div className="pt-1 flex items-center gap-2 text-xs text-muted-foreground">
                  <Activity className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    {availableNowCount > 0
                      ? <><strong className="text-foreground">{availableNowCount} staff</strong> available for allocation across {headcountDeptBars.length} {headcountDeptBars.length === 1 ? 'department' : 'departments'}</>
                      : <><strong className="text-emerald-600 dark:text-emerald-400">All {totalHeadcount} staff</strong> are currently allocated</>
                    }
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        );
      }

      // ── Quick Links — Executive (F17) ────────────────────────────────────────
      case 'quick_links_executive': {
        // Finance-required links are locked if finance integration not connected
        const financeConnected = financeStatus.isConnected;
        // Planner/timesheet required for utilisation
        const utilisationConnected = plannerStatus.isConnected || timesheetStatus.isConnected;

        const quickLinks: {
          id: string;
          label: string;
          description: string;
          route: string;
          icon: React.ReactNode;
          lockedReason?: string; // if set, link is hidden until integration is connected
        }[] = [
          {
            id: 'profitability-report',
            label: 'Profitability Report',
            description: 'Gross margin and revenue trends across the portfolio',
            route: 'reports/profitability',
            icon: <BarChart2 className="h-5 w-5" />,
            lockedReason: financeConnected ? undefined : 'Requires finance integration',
          },
          {
            id: 'wip-report',
            label: 'WIP Report',
            description: 'Work in progress balances and aging across all projects',
            route: 'reports/wip',
            icon: <BookOpen className="h-5 w-5" />,
            lockedReason: financeConnected ? undefined : 'Requires finance integration',
          },
          {
            id: 'project-pl',
            label: 'Project P&L',
            description: 'Project-level profit and loss with actuals vs budget',
            route: 'reports/project-pl',
            icon: <FileText className="h-5 w-5" />,
            lockedReason: financeConnected ? undefined : 'Requires finance integration',
          },
          {
            id: 'budget-variance',
            label: 'Budget Variance',
            description: 'Cost and revenue variance vs budget across all projects',
            route: 'reports/budget-variance',
            icon: <TrendingUp className="h-5 w-5" />,
          },
          {
            id: 'utilisation',
            label: 'Utilisation',
            description: 'Staff utilisation rates, bench strength, and capacity',
            route: 'utilisation',
            icon: <Gauge className="h-5 w-5" />,
            lockedReason: utilisationConnected ? undefined : 'Requires planner or timesheet integration',
          },
          {
            id: 'portfolio',
            label: 'Portfolio View',
            description: 'Full project portfolio with health, status, and financials',
            route: 'portfolio',
            icon: <Layers className="h-5 w-5" />,
          },
          {
            id: 'audit-log',
            label: 'Audit Log',
            description: 'System-wide audit trail of all actions and changes',
            route: 'audit-log',
            icon: <ClipboardList className="h-5 w-5" />,
          },
        ];

        return (
          <Card key="quick_links_executive" className="col-span-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Quick Links</CardTitle>
              <CardDescription>Shortcuts to key reports and views</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 items-stretch">
                {quickLinks.filter(link => !link.lockedReason).map(({ id, label, description, route, icon }) => (
                  <button
                    key={id}
                    onClick={() => handleNavigate(route)}
                    className="group relative flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-4 text-left transition-colors cursor-pointer hover:border-primary/50 hover:bg-accent/50 h-full justify-between"
                  >
                    {/* Top: icon + label + description */}
                    <div className="flex flex-col items-start gap-2 w-full">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary group-hover:bg-primary/20">
                        {icon}
                      </span>
                      <div className="space-y-0.5">
                        <p className="text-sm font-medium leading-tight text-foreground">{label}</p>
                        <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{description}</p>
                      </div>
                    </div>

                    {/* Bottom: arrow always pinned */}
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary mt-1" />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      }



      // ══════════════════════════════════════════════════════════════════════════
      // F29 — Finance Pulse (4 KPI tiles)
      // Migrated from FinanceDashboardShell — portfolio-level financial KPIs.
      // Off by default — user must enable in settings cog.
      // ══════════════════════════════════════════════════════════════════════════
      case 'finance_pulse': {
        const wipDelta      = totalWip - priorWip;
        const revDelta      = revenueRecognisedMtd - priorRevRecognised;
        const overdueDelta  = finOverdueCount - priorFinOverdueCount;
        const deferredDelta = totalDeferred - priorDeferred;
        return (
          <div key="finance_pulse" className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title="Total WIP"
              value={<SensitiveData><span>{fmtCurrency(totalWip)}</span></SensitiveData>}
              icon={WalletCards}
              accentVariant="info"
              trend={wipDelta !== 0 ? { value: Math.abs(wipDelta), isPositive: wipDelta < 0, displayValue: fmtCurrency(Math.abs(wipDelta)) } : undefined}
              description="vs last month"
            />
            {!isPulseTileHiddenForRelease('revenue_recognised_mtd') && (
              <KpiCard
                title="Revenue Recognised MTD"
                value={<SensitiveData><span>{fmtCurrency(revenueRecognisedMtd)}</span></SensitiveData>}
                icon={TrendingUp}
                accentVariant="info"
                trend={revDelta !== 0 ? { value: Math.round(Math.abs(revDelta)), isPositive: revDelta >= 0 } : undefined}
                description="vs last month"
              />
            )}
            <KpiCard
              title="Invoices Overdue"
              value={finOverdueCount}
              icon={finOverdueCount > 0 ? AlertCircle : CheckCircle2}
              accentVariant={finOverdueCount === 0 ? 'success' : finOverdueCount <= 2 ? 'warning' : 'error'}
              trend={overdueDelta !== 0 ? { value: Math.abs(overdueDelta), isPositive: overdueDelta <= 0 } : undefined}
              description={finOverdueCount > 0 ? undefined : 'All invoices up to date'}
            >
              {finOverdueCount > 0 && finOverdueAmount > 0 && (
                <div className="text-xs text-muted-foreground">
                  <SensitiveData><span>{fmtCurrency(finOverdueAmount)} outstanding</span></SensitiveData>
                </div>
              )}
            </KpiCard>
            {!isPulseTileHiddenForRelease('deferred_revenue') && (
              <KpiCard
                title="Deferred Revenue"
                value={<SensitiveData><span>{fmtCurrency(totalDeferred)}</span></SensitiveData>}
                icon={CircleDollarSign}
                accentVariant="info"
                trend={deferredDelta !== 0 ? { value: Math.round(Math.abs(deferredDelta)), isPositive: deferredDelta <= 0 } : undefined}
                description="vs last month"
              />
            )}
          </div>
        );
      }

      // ══════════════════════════════════════════════════════════════════════════
      // F30 — WIP Aging Overview
      // Migrated from FinanceDashboardShell — full portfolio WIP aging table.
      // Off by default — user must enable in settings cog.
      // ══════════════════════════════════════════════════════════════════════════
      case 'wip_aging_overview': {
        return (
          <Card key="wip_aging_overview">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                    WIP Aging Overview
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    Click a band to filter the project table
                    {wipView.band && (
                      <button className="ml-2 text-primary underline underline-offset-2 text-xs" onClick={() => wipView.toggleBand(null)}>
                        Clear filter
                      </button>
                    )}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleNavigate('wip-reporting')} className="gap-1 text-xs text-muted-foreground">
                  View full WIP report <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {wipView.positiveWipCount === 0 ? (
                <div className="flex items-center gap-2 rounded-md bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-3 py-2.5 text-xs text-success-700 dark:text-success-300">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  No outstanding WIP — all work is billed up to date
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                    <span>Portfolio total WIP:</span>
                    <SensitiveData><span className="font-semibold text-foreground">{fmtCurrency(portfolioWipTotal)}</span></SensitiveData>
                  </div>
                  <AgedWipStrip
                    wipItems={[]}
                    bandTotals={portfolioWipBands}
                    writtenOffIds={new Set()}
                    activeBand={wipView.band}
                    onBandClick={(band) => wipView.toggleBand(band)}
                  />
                </>
              )}
              <div className="overflow-x-auto -mx-6">
                <Table>
                  <TableHeader divided>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Project / Client</TableHead>
                      <TableHead>PM</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">WIP Amount</TableHead>
                      <TableHead>Aging</TableHead>
                      <TableHead className="text-center">Ready to Bill</TableHead>
                      <TableHead className="text-right">Over Billing</TableHead>
                      <TableHead className="text-right">Under Billing</TableHead>
                      <TableHead className="text-right">Written Off</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wipView.tableRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="py-8 text-center text-xs text-muted-foreground">No projects in this aging band</TableCell>
                      </TableRow>
                    ) : (
                      [...wipView.tableRows]
                        .sort((a, b) => (b.agingDays ?? 0) - (a.agingDays ?? 0))
                        .map((r) => {
                          const isOverBilled = (r.overBilling ?? 0) > 0;
                          return (
                            <TableRow key={r.projectId} className={cn('cursor-pointer', isOverBilled ? 'bg-destructive/5' : '')} onClick={() => handleNavigate(`projects/${r.projectId}`)}>
                              <TableCell>
                                <div className="font-medium text-xs leading-snug">{r.projectName}</div>
                                <div className="text-xs text-muted-foreground">{r.clientName}</div>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.pmName ?? '—'}</TableCell>
                              <TableCell>{r.projectType ? <Badge variant="outline" className="text-xs font-normal whitespace-nowrap">{r.projectType}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                              <TableCell className="text-right">
                                <SensitiveData><span className={cn('text-xs font-medium tabular-nums', isOverBilled ? 'text-destructive' : 'text-foreground')}>{formatCurrency(r.wipAmount)}</span></SensitiveData>
                                {isOverBilled && <div className="text-[10px] text-destructive font-semibold">OVER-BILLED</div>}
                              </TableCell>
                              <TableCell><AgingBadge days={r.agingDays ?? 0} /></TableCell>
                              <TableCell className="text-center">
                                {r.readyToBill ? (
                                  <button className="inline-flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap" onClick={e => { e.stopPropagation(); handleNavigate(`projects/${r.projectId}?tab=billing`); }}>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-success-600" />Raise Invoice
                                  </button>
                                ) : <span className="text-xs text-muted-foreground">—</span>}
                              </TableCell>
                              <TableCell className="text-right">{(r.overBilling ?? 0) > 0 ? <SensitiveData><span className="text-xs font-medium text-destructive tabular-nums">{formatCurrency(r.overBilling!)}</span></SensitiveData> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                              <TableCell className="text-right">{(r.underBilling ?? 0) > 0 ? <SensitiveData><span className="text-xs font-medium text-warning-600 tabular-nums">{formatCurrency(r.underBilling!)}</span></SensitiveData> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                              <TableCell className="text-right">{(r.writtenOffAmount ?? 0) > 0 ? <SensitiveData><span className="text-xs text-muted-foreground tabular-nums">{formatCurrency(r.writtenOffAmount!)}</span></SensitiveData> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                            </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
              </div>
              {wipView.isPaged && wipView.tableTotalCount > 0 && (
                <div className="flex items-center justify-between border-t px-1 pt-3 text-xs text-muted-foreground">
                  <span>
                    Showing {(wipView.page - 1) * wipView.pageSize + 1}–
                    {Math.min(wipView.page * wipView.pageSize, wipView.tableTotalCount)} of {wipView.tableTotalCount} projects
                    {wipView.band && ` in ${wipView.band} days`}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 px-2 gap-0.5" disabled={wipView.page <= 1 || wipView.pageLoading} onClick={() => wipView.setPage(wipView.page - 1)}>
                      <ChevronUp className="h-3 w-3 rotate-[-90deg]" />Prev
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 gap-0.5" disabled={wipView.page >= wipView.pageCount || wipView.pageLoading} onClick={() => wipView.setPage(wipView.page + 1)}>
                      Next<ChevronDown className="h-3 w-3 rotate-[-90deg]" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 rounded-md bg-muted/30 border px-4 py-2.5 text-xs">
                <div className="flex items-center gap-1.5 cursor-pointer hover:text-foreground text-muted-foreground transition-colors" onClick={() => handleNavigate('wip-reporting')}>
                  <CheckCircle2 className="h-3.5 w-3.5 text-success-600" />
                  <span className="font-medium text-foreground">{wipView.readyToBillCount}</span><span>ready to bill</span>
                  {wipView.readyToBillAmount > 0 && <SensitiveData><span className="text-muted-foreground">({fmtCurrency(wipView.readyToBillAmount)})</span></SensitiveData>}
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                  <span className="font-medium text-foreground">{wipView.overBilledCount}</span><span>over-billed</span>
                  {wipView.overBilledAmount > 0 && <SensitiveData><span className="text-destructive">({fmtCurrency(wipView.overBilledAmount)})</span></SensitiveData>}
                </div>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <TrendingDown className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="font-medium text-foreground">{wipView.writtenOffCount}</span><span>written off MTD</span>
                  {wipView.writtenOffAmount > 0 && <SensitiveData><span className="text-muted-foreground">({fmtCurrency(wipView.writtenOffAmount)})</span></SensitiveData>}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      }

      // ══════════════════════════════════════════════════════════════════════════
      // F31 — Revenue Recognition Status
      // Migrated from FinanceDashboardShell — portfolio-level revenue recognition.
      // Off by default — user must enable in settings cog.
      // ══════════════════════════════════════════════════════════════════════════
      case 'revenue_recognition_status': {
        const urgencyBorder = monthEndBorderClass();
        if (revActiveProjectCount === 0) {
          return (
            <Card key="revenue_recognition_status" className={cn(urgencyBorder && `border-2 ${urgencyBorder}`)}>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChartIcon className="h-4 w-4 text-muted-foreground" />Revenue Recognition Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground">
                  <PieChartIcon className="h-10 w-10 opacity-25" />
                  <p className="text-sm">No revenue recognition data available</p>
                </div>
              </CardContent>
            </Card>
          );
        }
        return (
          <Card key="revenue_recognition_status" className={cn(urgencyBorder && `border-2 ${urgencyBorder}`)}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <PieChartIcon className="h-4 w-4 text-muted-foreground" />Revenue Recognition Status
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    {revActiveProjectCount} active project{revActiveProjectCount !== 1 ? 's' : ''} — by recognition method
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleNavigate('reports/profitability')} className="gap-1 text-xs text-muted-foreground">
                  View revenue report <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <MonthEndBanner />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <CompactKpiCard
                  title="Recognised"
                  value={<SensitiveData>{fmtCurrency(revTotalRecognised)}</SensitiveData>}
                  icon={CheckCircle2}
                  accentVariant="success"
                  description={revGrandTotal > 0 ? `${((revTotalRecognised / revGrandTotal) * 100).toFixed(0)}% of total` : undefined}
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="To Recognise"
                  value={<SensitiveData>{fmtCurrency(revToRecognise)}</SensitiveData>}
                  icon={Receipt}
                  accentVariant="info"
                  description={revGrandTotal > 0 ? `${((revToRecognise / revGrandTotal) * 100).toFixed(0)}% of total` : undefined}
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Deferred"
                  value={<SensitiveData>{fmtCurrency(revDeferred)}</SensitiveData>}
                  icon={AlertTriangle}
                  accentVariant={revDeferred > 0 ? 'warning' : 'info'}
                  description={revGrandTotal > 0 ? `${((revDeferred / revGrandTotal) * 100).toFixed(0)}% of total` : undefined}
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Unbilled"
                  value={<SensitiveData>{fmtCurrency(revUnbilled)}</SensitiveData>}
                  icon={Banknote}
                  accentVariant="info"
                  description={revGrandTotal > 0 ? `${((revUnbilled / revGrandTotal) * 100).toFixed(0)}% of total` : undefined}
                  className="shadow-none"
                />
              </div>
              {methodBreakdown.length > 0 && (
                <div className="overflow-x-auto -mx-6 -mb-6">
                  <Table>
                    <TableHeader divided>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Projects</TableHead>
                        <TableHead className="text-right">Contract Value</TableHead>
                        <TableHead className="text-right">Recognised</TableHead>
                        <TableHead className="text-right">Deferred</TableHead>
                        <TableHead className="text-right">Unbilled</TableHead>
                        <TableHead className="text-right">Recognition %</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {methodBreakdown.map(m => (
                        <TableRow key={m.method}>
                          <TableCell><span className="text-xs font-medium">{m.label}</span></TableCell>
                          <TableCell className="text-right text-xs text-muted-foreground">{m.count}</TableCell>
                          <TableCell className="text-right"><SensitiveData><span className="text-xs tabular-nums">{fmtCurrency(m.budgetRevenue)}</span></SensitiveData></TableCell>
                          <TableCell className="text-right"><SensitiveData><span className="text-xs text-success-600 font-medium tabular-nums">{fmtCurrency(m.revenueRecognised)}</span></SensitiveData></TableCell>
                          <TableCell className="text-right"><SensitiveData><span className="text-xs text-warning-600 tabular-nums">{fmtCurrency(m.deferredRevenue)}</span></SensitiveData></TableCell>
                          <TableCell className="text-right"><SensitiveData><span className="text-xs text-purple-600 tabular-nums">{fmtCurrency(m.unbilledRevenue)}</span></SensitiveData></TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-col items-end gap-1">
                              <span className={cn('text-xs font-medium tabular-nums', m.recognitionPct < 50 ? 'text-warning-600' : m.recognitionPct >= 90 ? 'text-success-600' : 'text-foreground')}>{m.recognitionPct.toFixed(0)}%</span>
                              <Progress value={m.recognitionPct} className="h-1 w-16" />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {revActionList.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Action Required</p>
                  {revActionList.map((item, i) => (
                    <div key={i} className={cn('flex items-start gap-2 rounded-md px-3 py-2 text-xs', item.urgency === 'red' ? 'bg-destructive/10 text-destructive' : item.urgency === 'amber' ? 'bg-warning-50 text-warning-700' : 'bg-blue-50 text-blue-700')}>
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      {item.text}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      }

      // ══════════════════════════════════════════════════════════════════════════
      // F32 — Billing Pipeline
      // Migrated from FinanceDashboardShell — portfolio-level billing pipeline.
      // Off by default — user must enable in settings cog.
      // ══════════════════════════════════════════════════════════════════════════
      case 'billing_pipeline': {
        const tabs = [
          { id: 'ready' as const,   label: 'Ready to Raise',  count: wipView.readyToBillCount, amount: wipView.readyToBillAmount },
          { id: 'coming' as const,  label: 'Coming Up',       count: wipView.upcomingCount,    amount: wipView.upcomingAmount },
          { id: 'overdue' as const, label: 'Overdue / Chase', count: wipView.overdueCount,     amount: wipView.overdueAmount },
        ];
        const activeTabData =
          billingQueueTab === 'ready'   ? wipView.readyToBill.slice(0, MAX_QUEUE_ROWS) :
          billingQueueTab === 'coming'  ? wipView.upcoming.slice(0, MAX_QUEUE_ROWS) :
          wipView.overdue.slice(0, MAX_QUEUE_ROWS);
        const activeTabTotal =
          billingQueueTab === 'ready'   ? wipView.readyToBillCount :
          billingQueueTab === 'coming'  ? wipView.upcomingCount :
          wipView.overdueCount;
        return (
          <Card key="billing_pipeline">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Receipt className="h-4 w-4 text-muted-foreground" />Billing Pipeline
                  </CardTitle>
                  <CardDescription className="mt-0.5">Ready to raise, upcoming, and overdue invoices</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => handleNavigate('reports/wip')} className="gap-1 text-xs text-muted-foreground">
                  View invoicing <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <button className="text-left" onClick={() => setBillingQueueTab('ready')}>
                  <CompactKpiCard
                    title="Ready to Raise"
                    value={String(tabs.find(t => t.id === 'ready')!.count)}
                    icon={CheckCircle2}
                    description={tabs.find(t => t.id === 'ready')!.amount > 0 ? fmtCurrency(tabs.find(t => t.id === 'ready')!.amount) : undefined}
                    accentVariant={tabs.find(t => t.id === 'ready')!.count > 0 ? 'success' : 'info'}
                    className="shadow-none"
                  />
                </button>
                <button className="text-left" onClick={() => setBillingQueueTab('coming')}>
                  <CompactKpiCard
                    title="Coming Up"
                    value={String(tabs.find(t => t.id === 'coming')!.count)}
                    icon={CalendarClock}
                    description={tabs.find(t => t.id === 'coming')!.amount > 0 ? fmtCurrency(tabs.find(t => t.id === 'coming')!.amount) : undefined}
                    accentVariant="info"
                    className="shadow-none"
                  />
                </button>
                <button className="text-left" onClick={() => setBillingQueueTab('overdue')}>
                  <CompactKpiCard
                    title="Overdue / Chase"
                    value={String(tabs.find(t => t.id === 'overdue')!.count)}
                    icon={AlertCircle}
                    description={tabs.find(t => t.id === 'overdue')!.amount > 0 ? fmtCurrency(tabs.find(t => t.id === 'overdue')!.amount) : undefined}
                    accentVariant={tabs.find(t => t.id === 'overdue')!.count > 0 ? 'error' : 'success'}
                    className="shadow-none"
                  />
                </button>
              </div>
              {activeTabData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground border rounded-md bg-muted/10">
                  <CheckCircle2 className={cn('h-8 w-8 opacity-40', billingQueueTab === 'overdue' ? 'text-success-500' : 'text-muted-foreground')} />
                  <p className="text-sm font-medium">
                    {billingQueueTab === 'ready' ? 'No projects ready to raise right now' : billingQueueTab === 'coming' ? 'No billing events in the next 30 days' : 'No overdue invoices — all up to date'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto -mx-6 -mb-6">
                    <Table>
                      <TableHeader divided>
                        <TableRow className="hover:bg-transparent">
                          <TableHead>Project / Client</TableHead>
                          <TableHead>Type</TableHead>
                          {billingQueueTab === 'ready' && <><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">WIP Balance</TableHead><TableHead>PM</TableHead><TableHead>Trigger</TableHead><TableHead>Action</TableHead></>}
                          {billingQueueTab === 'coming' && <><TableHead>Due Date</TableHead><TableHead className="text-right">Days Until</TableHead><TableHead className="text-right">Amount</TableHead></>}
                          {billingQueueTab === 'overdue' && <><TableHead>Due Date</TableHead><TableHead className="text-right">Days Overdue</TableHead><TableHead className="text-right">Amount</TableHead></>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeTabData.map((r) => {
                          const isTam = r.projectType === 'time-and-materials' || r.projectType === 'retainer';
                          const dueDays = r.nextBillingEvent ? daysUntil(r.nextBillingEvent) : null;
                          return (
                            <TableRow key={r.projectId} className={cn('cursor-pointer', billingQueueTab === 'overdue' ? 'bg-destructive/5' : '')} onClick={() => handleNavigate(`projects/${r.projectId}?tab=billing`)}>
                              <TableCell><div className="text-xs font-medium leading-snug">{r.projectName}</div><div className="text-xs text-muted-foreground">{r.clientName}</div></TableCell>
                              <TableCell>{r.projectType ? <Badge variant="outline" className="text-[10px] font-normal whitespace-nowrap">{r.projectType}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                              {billingQueueTab === 'ready' && (
                                <>
                                  <TableCell className="text-right"><SensitiveData><span className="text-xs font-medium tabular-nums text-success-600">{fmtCurrency(r.nextBillingEventAmount ?? 0)}{isTam && <span className="text-muted-foreground font-normal ml-0.5">Est.</span>}</span></SensitiveData></TableCell>
                                  <TableCell className="text-right"><SensitiveData><span className="text-xs tabular-nums">{fmtCurrency(r.wipAmount)}</span></SensitiveData></TableCell>
                                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.pmName ?? '—'}</TableCell>
                                  <TableCell>{r.nextBillingEventTriggerType ? <Badge variant="outline" className="text-[10px] font-normal whitespace-nowrap">{r.nextBillingEventTriggerType}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                  <TableCell><button className="text-xs text-primary hover:underline whitespace-nowrap" onClick={e => { e.stopPropagation(); handleNavigate(`projects/${r.projectId}?tab=billing`); }}>Raise Invoice →</button></TableCell>
                                </>
                              )}
                              {billingQueueTab === 'coming' && (
                                <>
                                  <TableCell className="text-xs text-foreground whitespace-nowrap">{fmtDate(r.nextBillingEvent)}</TableCell>
                                  <TableCell className="text-right">{dueDays !== null ? <span className={cn('text-xs font-medium tabular-nums', dueDays <= 7 ? 'text-warning-600' : 'text-foreground')}>{dueDays}d</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                  <TableCell className="text-right"><SensitiveData><span className="text-xs tabular-nums">{fmtCurrency(r.nextBillingEventAmount ?? 0)}{isTam && <span className="text-muted-foreground ml-0.5">Est.</span>}</span></SensitiveData></TableCell>
                                </>
                              )}
                              {billingQueueTab === 'overdue' && (
                                <>
                                  <TableCell className="text-xs text-destructive whitespace-nowrap">{fmtDate(r.nextBillingEvent)}</TableCell>
                                  <TableCell className="text-right">{dueDays !== null ? <span className="text-xs font-medium text-destructive tabular-nums">{Math.abs(dueDays)}d overdue</span> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                                  <TableCell className="text-right"><SensitiveData><span className="text-xs font-medium text-destructive tabular-nums">{fmtCurrency(r.nextBillingEventAmount ?? 0)}</span></SensitiveData></TableCell>
                                </>
                              )}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  {activeTabTotal > MAX_QUEUE_ROWS && (
                    <div className="flex justify-end">
                      <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 text-muted-foreground" onClick={() => handleNavigate('reports/wip')}>
                        View all {activeTabTotal} ({activeTabTotal - MAX_QUEUE_ROWS} more) <ArrowRight className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      }

      // ══════════════════════════════════════════════════════════════════════════
      // F33 — Month-End Readiness
      // Migrated from FinanceDashboardShell — 8-item month-end checklist.
      // Off by default — user must enable in settings cog.
      // ══════════════════════════════════════════════════════════════════════════
      case 'month_end_readiness': {
        const urgencyBorder = monthEndBorderClass();
        const progressState = finProgressPct === 100 ? 'complete' : finProgressPct >= 50 ? 'close' : 'in-progress';
        const progressConfig = {
          'complete':    { text: '✅ All close tasks complete — ready for sign-off', colour: 'text-success-600' },
          'close':       { text: `Getting close — ${checklistItems.length - finCompleteCount} item${checklistItems.length - finCompleteCount !== 1 ? 's' : ''} remaining`, colour: 'text-blue-600' },
          'in-progress': { text: 'Month-end close in progress', colour: 'text-warning-600' },
        };
        return (
          <Card key="month_end_readiness" className={cn(urgencyBorder && `border-2 ${urgencyBorder}`)}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-muted-foreground" /><CardTitle className="text-base">Month-End Readiness</CardTitle></div>
              <CardDescription className="mt-0.5">{finCompleteCount} of {checklistItems.length} tasks complete</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <MonthEndBanner />
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs">
                  <span className={cn('font-medium', progressConfig[progressState].colour)}>{progressConfig[progressState].text}</span>
                  <span className="text-muted-foreground tabular-nums">{finProgressPct}%</span>
                </div>
                <Progress value={finProgressPct} className="h-2" />
              </div>
              <div className="space-y-1.5 pt-1">
                {checklistItems.map((item, i) => {
                  const isComplete = item.state === 'complete';
                  const isAction   = item.state === 'action';
                  const isBlocked  = item.state === 'blocked';
                  return (
                    <div
                      key={i}
                      className={cn('flex items-start gap-2.5 rounded-md px-3 py-2 text-xs transition-colors',
                        isComplete ? 'bg-success-50 dark:bg-success-950/20 text-success-700 dark:text-success-300' :
                        isAction   ? 'bg-warning-50 dark:bg-warning-950/20 text-warning-700 dark:text-warning-300' :
                                     'bg-muted/30 text-muted-foreground',
                        item.actionRoute ? 'cursor-pointer hover:opacity-90' : '',
                      )}
                      onClick={item.actionRoute ? () => handleNavigate(item.actionRoute!) : undefined}
                    >
                      {isComplete && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-0.5 text-success-500" />}
                      {isAction   && <AlertCircle  className="h-3.5 w-3.5 shrink-0 mt-0.5 text-warning-500" />}
                      {isBlocked  && <Lock         className="h-3.5 w-3.5 shrink-0 mt-0.5 text-muted-foreground" />}
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{item.label}</span>
                        {item.detail && <span className="ml-1 font-normal opacity-80">— {item.detail}</span>}
                        {isBlocked && item.actionRoute && <span className="ml-1 underline underline-offset-2 opacity-70"> Connect now</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      }

      // ══════════════════════════════════════════════════════════════════════════
      // F35 — Over & Under Billing
      // Migrated from FinanceDashboardShell — over/under billing tables.
      // Off by default — user must enable in settings cog.
      // ══════════════════════════════════════════════════════════════════════════
      case 'over_under_billing': {
        return (
          <Card key="over_under_billing">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Activity className="h-4 w-4 text-muted-foreground" /><CardTitle className="text-base">Over &amp; Under Billing</CardTitle></div>
                <Button variant="ghost" size="sm" onClick={() => handleNavigate('wip-reporting')} className="gap-1 text-xs text-muted-foreground">WIP Report <ArrowRight className="h-3.5 w-3.5" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <CompactKpiCard
                  title="Total Over-Billed"
                  value={<SensitiveData>{fmtCurrency(totalOverBilled)}</SensitiveData>}
                  icon={BadgeAlert}
                  accentVariant={totalOverBilled > 0 ? 'error' : 'info'}
                  description={`${wipView.overBilledCount} project${wipView.overBilledCount !== 1 ? 's' : ''}`}
                  className="shadow-none"
                />
                <CompactKpiCard
                  title="Total Under-Billed"
                  value={<SensitiveData>{fmtCurrency(totalUnderBilled)}</SensitiveData>}
                  icon={AlertTriangle}
                  accentVariant={totalUnderBilled > 0 ? 'warning' : 'info'}
                  description={`${wipView.underBilledCount} project${wipView.underBilledCount !== 1 ? 's' : ''}`}
                  className="shadow-none"
                />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-semibold text-destructive uppercase tracking-wide flex items-center gap-1.5"><BadgeAlert className="h-3.5 w-3.5" />Over-Billed</p>
                {wipView.overBilled.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-md bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-3 py-2 text-xs text-success-700 dark:text-success-300">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />No over-billed projects
                  </div>
                ) : (
                  <div className="space-y-1">
                    {wipView.overBilled.map(r => {
                      return (
                        <div key={r.projectId} className="flex items-center justify-between gap-2 rounded-md border bg-destructive/5 border-destructive/15 px-3 py-2 cursor-pointer hover:bg-destructive/10 transition-colors" onClick={() => handleNavigate(`projects/${r.projectId}?tab=financials`)}>
                          <div className="min-w-0">
                            <div className="text-xs font-medium truncate">{r.projectName}</div>
                            <div className="text-[10px] text-muted-foreground">{r.clientName} · {r.pmName ?? '—'}</div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0 text-xs">
                            <div className="text-right">
                              <SensitiveData><span className="font-medium text-destructive tabular-nums">{fmtCurrency(r.overBilling!)}</span></SensitiveData>
                            </div>
                            {r.projectType && <Badge variant="outline" className="text-[10px] font-normal whitespace-nowrap border-destructive/30 text-destructive">{r.projectType}</Badge>}
                          </div>
                        </div>
                      );
                    })}
                    {wipView.overBilledCount > wipView.overBilled.length && (
                      <button className="text-[10px] text-muted-foreground hover:underline" onClick={() => handleNavigate('wip-reporting')}>
                        +{wipView.overBilledCount - wipView.overBilled.length} more in the WIP report
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="border-t" />
              <div className="space-y-2">
                <p className="text-xs font-semibold text-warning-600 uppercase tracking-wide flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5" />Under-Billed<span className="text-muted-foreground font-normal normal-case tracking-normal">(cash at risk)</span>
                </p>
                {wipView.underBilled.length === 0 ? (
                  <div className="flex items-center gap-2 rounded-md bg-success-50 dark:bg-success-950/20 border border-success-200 dark:border-success-800 px-3 py-2 text-xs text-success-700 dark:text-success-300">
                    <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />No under-billed projects above threshold
                  </div>
                ) : (
                  <div className="space-y-1">
                    {wipView.underBilled.map(r => {
                      const isTam = r.projectType === 'time-and-materials' || r.projectType === 'retainer';
                      const urgency = isTam ? 'High' : r.projectType === 'fixed-price' ? 'Medium' : 'Lower';
                      const urgencyColour = urgency === 'High' ? 'text-destructive' : urgency === 'Medium' ? 'text-warning-600' : 'text-muted-foreground';
                      return (
                        <div key={r.projectId} className="flex items-center justify-between gap-2 rounded-md border bg-warning-50 dark:bg-warning-950/20 border-warning-200 dark:border-warning-800 px-3 py-2 cursor-pointer hover:bg-warning-100 dark:hover:bg-warning-950/30 transition-colors" onClick={() => handleNavigate(`projects/${r.projectId}?tab=financials`)}>
                          <div className="min-w-0">
                            <div className="text-xs font-medium truncate">{r.projectName}</div>
                            <div className="text-[10px] text-muted-foreground">{r.clientName} · {r.pmName ?? '—'} · Aged {r.agingDays ?? 0}d</div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0 text-xs">
                            <div className="text-right">
                              <SensitiveData><span className="font-medium text-warning-700 tabular-nums">{fmtCurrency(r.underBilling!)}</span></SensitiveData>
                              <div className={cn('text-[10px] font-medium', urgencyColour)}>{urgency} urgency</div>
                            </div>
                            {r.projectType && <Badge variant="outline" className="text-[10px] font-normal whitespace-nowrap border-warning-300 text-warning-700">{r.projectType}</Badge>}
                          </div>
                        </div>
                      );
                    })}
                    {wipView.underBilledCount > wipView.underBilled.length && (
                      <button className="text-[10px] text-muted-foreground hover:underline" onClick={() => handleNavigate('wip-reporting')}>
                        +{wipView.underBilledCount - wipView.underBilled.length} more in the WIP report
                      </button>
                    )}
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground">Noise threshold: projects under £{UNDER_BILLING_THRESHOLD.toLocaleString()} excluded</p>
              </div>
            </CardContent>
          </Card>
        );
      }

      // ══════════════════════════════════════════════════════════════════════════
      // F36 — Project Financial Summary
      // Migrated from FinanceDashboardShell — full portfolio financial table.
      // Off by default — user must enable in settings cog.
      // ══════════════════════════════════════════════════════════════════════════
      case 'project_financial_summary': {
        const hasFilterActive = financeFilter.health !== 'all' || financeFilter.projectType !== 'all' || financeFilter.signal !== 'all';
        return (
          <Card key="project_financial_summary">
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4 text-muted-foreground" />Project Financial Summary</CardTitle>
                  <CardDescription className="mt-0.5">{summaryTotalCount} active project{summaryTotalCount !== 1 ? 's' : ''}{hasFilterActive && ' (filtered)'} — sorted by WIP amount</CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button variant={hasFilterActive ? 'default' : 'outline'} size="sm" className="h-7 gap-1 text-xs" onClick={() => setShowFilters(v => !v)}>
                    <Filter className="h-3 w-3" />Filters{hasFilterActive && <Badge className="h-4 w-4 p-0 flex items-center justify-center text-[9px] ml-0.5">!</Badge>}
                  </Button>
                  {hasFilterActive && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => updateFinanceFilter({ health: 'all', projectType: 'all', signal: 'all' })}>
                      Clear filters
                    </Button>
                  )}
                </div>
              </div>
              {showFilters && (
                <div className="mt-3 flex flex-wrap gap-3 rounded-md border bg-muted/20 p-3">
                  <div className="flex flex-col gap-1 min-w-[120px]">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Health</label>
                    <select className="rounded border bg-background px-2 py-1 text-xs" value={financeFilter.health} onChange={e => updateFinanceFilter({ health: e.target.value })}>
                      <option value="all">All</option><option value="red">Critical</option><option value="amber">At Risk</option><option value="green">Healthy</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1 min-w-[140px]">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Project Type</label>
                    <select className="rounded border bg-background px-2 py-1 text-xs" value={financeFilter.projectType} onChange={e => updateFinanceFilter({ projectType: e.target.value })}>
                      <option value="all">All</option><option value="fixed-price">Fixed Price</option><option value="time-and-materials">T&amp;M</option><option value="retainer">Retainer</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1 min-w-[160px]">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Signal</label>
                    <select className="rounded border bg-background px-2 py-1 text-xs" value={financeFilter.signal} onChange={e => updateFinanceFilter({ signal: e.target.value })}>
                      <option value="all">All</option><option value="Margin Risk">Margin Risk</option><option value="Over-Billed">Over-Billed</option><option value="Unrecognised">Unrecognised Revenue</option><option value="Rev Under">Rev Under</option><option value="WIP High">WIP High</option>
                    </select>
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Columns</label>
                    <div className="flex flex-wrap gap-1.5">
                      {[
                        { label: 'PM', state: showPm, set: setShowPm },
                        { label: 'Type', state: showType, set: setShowType },
                        { label: 'Method', state: showMethod, set: setShowMethod },
                        { label: 'Budget Rev', state: showBudgetRev, set: setShowBudgetRev },
                        { label: 'Actual Sales', state: showActualSales, set: setShowActualSales },
                        { label: 'Rev Var %', state: showRevVar, set: setShowRevVar },
                        { label: 'Actual Costs', state: showActualCosts, set: setShowActualCosts },
                        { label: 'Completion', state: showCompletion, set: setShowCompletion },
                        { label: 'Health', state: showHealth, set: setShowHealth },
                      ].map(col => (
                        <button key={col.label} onClick={() => col.set(v => !v)} className={cn('rounded px-2 py-0.5 text-[10px] font-medium border transition-colors', col.state ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/40')}>{col.label}</button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {filteredSummaryRows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground px-4">
                  <Layers className="h-8 w-8 opacity-25 mb-2" />
                  <p className="text-sm font-medium">No projects match the current filters</p>
                  <Button variant="outline" size="sm" className="mt-3 gap-1" onClick={() => updateFinanceFilter({ health: 'all', projectType: 'all', signal: 'all' })}>Clear filters</Button>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-muted/30 z-10">Project / Client</TableHead>
                          {showPm          && <TableHead>PM</TableHead>}
                          {showType        && <TableHead>Type</TableHead>}
                          {showMethod      && <TableHead>Method</TableHead>}
                          {showBudgetRev   && <TableHead className="text-right">Budget Rev</TableHead>}
                          {showActualSales && <TableHead className="text-right">Actual Sales</TableHead>}
                          {showRevVar      && <TableHead className="text-right">Rev Var %</TableHead>}
                          {showActualCosts && <TableHead className="text-right">Actual Costs</TableHead>}
                          <TableHead className="text-right">Gross Margin %</TableHead>
                          <TableHead className="text-right">WIP Amount</TableHead>
                          {showCompletion  && <TableHead className="text-right">Completion</TableHead>}
                          {showHealth      && <TableHead>Health</TableHead>}
                          <TableHead>Signals</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {summaryPagedRows.map((r) => (
                          <TableRow key={r.id} className="cursor-pointer" onClick={() => handleNavigate(`projects/${r.id}`)}>
                            <TableCell className="sticky left-0 bg-inherit z-10">
                              <div className="font-medium text-xs leading-snug">{r.name}</div>
                              {r.clientNames && <div className="text-xs text-muted-foreground">{r.clientNames}</div>}
                            </TableCell>
                            {showPm          && <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.pmName}</TableCell>}
                            {showType        && <TableCell><Badge variant="outline" className="text-[10px] font-normal whitespace-nowrap">{r.projectType}</Badge></TableCell>}
                            {showMethod      && <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.recognitionMethod ?? '—'}</TableCell>}
                            {showBudgetRev   && <TableCell className="text-right"><SensitiveData><span className="text-xs tabular-nums">{fmtCurrency(r.budgetRevenue)}</span></SensitiveData></TableCell>}
                            {showActualSales && <TableCell className="text-right"><SensitiveData><span className="text-xs tabular-nums">{r.actualSales > 0 ? fmtCurrency(r.actualSales) : '—'}</span></SensitiveData></TableCell>}
                            {showRevVar      && <TableCell className="text-right"><span className={cn('text-xs tabular-nums font-medium', (r.grossMarginPct ?? 0) < 0 ? 'text-destructive' : 'text-foreground')}>—</span></TableCell>}
                            {showActualCosts && <TableCell className="text-right"><SensitiveData><span className="text-xs tabular-nums">{r.actualSales > 0 ? fmtCurrency(r.actualSales - (r.grossMarginPct ? r.actualSales * r.grossMarginPct / 100 : 0)) : '—'}</span></SensitiveData></TableCell>}
                            <TableCell className="text-right">
                              {r.grossMarginPct !== null && r.actualSales > 0 ? (
                                <span className={cn('text-xs font-medium tabular-nums', r.grossMarginPct < 0 ? 'text-destructive' : r.grossMarginPct < 20 ? 'text-warning-600' : 'text-success-600')}>{r.grossMarginPct.toFixed(2)}%</span>
                              ) : <span className="text-xs text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="text-right"><SensitiveData><span className="text-xs font-medium tabular-nums">{fmtCurrency(r.wipAmount)}</span></SensitiveData></TableCell>
                            {showCompletion && <TableCell className="text-right"><div className="flex flex-col items-end gap-1"><span className="text-xs tabular-nums">{r.completionPercent.toFixed(0)}%</span><Progress value={r.completionPercent} className="h-1 w-14" /></div></TableCell>}
                            {showHealth && <TableCell><FinRagBadge health={r.health} /></TableCell>}
                            <TableCell>
                              <div className="flex flex-wrap gap-1">
                                {r.signals.length > 0 ? r.signals.map(s => <SignalChip key={s.label} label={s.label} colour={s.colour} />) : <span className="text-xs text-muted-foreground">—</span>}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {summaryPageCount > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t text-xs text-muted-foreground">
                      <span>Showing {summaryPage * SUMMARY_PAGE_SIZE + 1}–{Math.min((summaryPage + 1) * SUMMARY_PAGE_SIZE, summaryTotalCount)} of {summaryTotalCount} projects</span>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="h-7 px-2 gap-0.5" disabled={summaryPage === 0} onClick={() => setSummaryPage(summaryPage - 1)}>
                          <ChevronUp className="h-3 w-3 rotate-[-90deg]" />Prev
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2 gap-0.5" disabled={summaryPage >= summaryPageCount - 1} onClick={() => setSummaryPage(summaryPage + 1)}>
                          Next<ChevronDown className="h-3 w-3 rotate-[-90deg]" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        );
      }

      // ══════════════════════════════════════════════════════════════════════════
      // Portfolio Forecast widget — opt-in, Executive only
      // Shows EAC summary KPIs + top 5 projects by EAC variance.
      // All data is mock — no API calls.
      // ══════════════════════════════════════════════════════════════════════════
      case 'portfolio_forecast': {
        // EAC method label for badge
        const eacMethodLabel =
          pfEacMethod === 'simple'
            ? 'Simple'
            : pfEacMethod === 'performance-based'
              ? 'Performance-Based'
              : 'Re-estimate';

        // HTC Data Coverage tile — only rendered when showHoursToComplete is On
        // When On but htcCoverage is null → amber "0% — no data submitted"
        const htcCoverage = mockPortfolioForecast.htcCoverage;

        return (
          <Card key="portfolio_forecast">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base">Portfolio Forecast</CardTitle>
              </div>
              <CardDescription>EAC cost summary and top projects by variance</CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* ── KPI tiles — 3-column grid ─────────────────────────────── */}
              <div className={`grid gap-3 ${pfShowHtc ? 'grid-cols-3' : 'grid-cols-3'}`}>

                {/* Tile 1 — Portfolio EAC Cost */}
                <div className="rounded-lg border bg-card p-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Portfolio EAC Cost
                  </p>
                  <p className="text-2xl font-bold tabular-nums">
                    <SensitiveData>{formatCurrency(mockPortfolioForecast.portfolioEacCost)}</SensitiveData>
                  </p>
                </div>

                {/* Tile 2 — Portfolio Forecast Revenue */}
                <div className="rounded-lg border bg-card p-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Portfolio Forecast Revenue
                  </p>
                  <p className="text-2xl font-bold tabular-nums">
                    <SensitiveData>{formatCurrency(mockPortfolioForecast.portfolioForecastRevenue)}</SensitiveData>
                  </p>
                </div>

                {/* Tile 3 — Portfolio Forecast Margin */}
                <div className="rounded-lg border bg-card p-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Portfolio Forecast Margin
                  </p>
                  <p className={`text-2xl font-bold tabular-nums ${mockPortfolioForecast.portfolioForecastMargin >= 0 ? 'text-success-600 dark:text-success-400' : 'text-destructive'}`}>
                    {mockPortfolioForecast.portfolioForecastMargin.toFixed(1)}%
                  </p>
                </div>

                {/* Tile 4 — EAC Method (badge, not a number) */}
                <div className="rounded-lg border bg-card p-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    EAC Method
                  </p>
                  <div className="pt-1">
                    <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground border border-border">
                      {eacMethodLabel}
                    </span>
                  </div>
                </div>

                {/* Tile 5 — Projects Over EAC Budget */}
                <div className="rounded-lg border bg-card p-4 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Projects Over EAC Budget
                  </p>
                  <p className={`text-2xl font-bold tabular-nums ${mockPortfolioForecast.projectsOverBudget > 0 ? 'text-destructive' : 'text-success-600 dark:text-success-400'}`}>
                    {mockPortfolioForecast.projectsOverBudget}
                  </p>
                </div>

                {/* Tile 6 — HTC Data Coverage (conditional on showHoursToComplete) */}
                {pfShowHtc && (
                  <div className="rounded-lg border bg-card p-4 space-y-1">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      HTC Data Coverage
                    </p>
                    {htcCoverage === null ? (
                      <div className="pt-1">
                        <span className="inline-flex items-center gap-1 rounded-full bg-warning-100 dark:bg-warning-950/30 border border-warning-200 dark:border-warning-800 px-2.5 py-0.5 text-xs font-medium text-warning-700 dark:text-warning-400">
                          0% — no data submitted
                        </span>
                      </div>
                    ) : (
                      <p className="text-2xl font-bold tabular-nums">{htcCoverage}%</p>
                    )}
                  </div>
                )}
              </div>

              {/* ── Top 5 projects by EAC variance ───────────────────────── */}
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                  Top 5 Projects by EAC Variance
                </p>
                <div className="overflow-x-auto -mx-6">
                  <Table>
                    <TableHeader divided>
                      <TableRow className="hover:bg-transparent">
                        <TableHead>Project Name</TableHead>
                        <TableHead>PM</TableHead>
                        <TableHead className="text-right">EAC Cost</TableHead>
                        <TableHead className="text-right">Budget</TableHead>
                        <TableHead className="text-right">Variance</TableHead>
                        <TableHead className="text-center">Health</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {mockTop5EacVariance.map((row) => (
                        <TableRow key={row.name} className="hover:bg-muted/40">
                          <TableCell className="font-medium text-sm">
                            <button
                              type="button"
                              className="text-left hover:underline hover:text-primary transition-colors cursor-pointer"
                              onClick={() => navigateHook(`/projects/${row.projectId}`)}
                            >
                              {row.name}
                            </button>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{row.pm}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">
                            <SensitiveData>{formatCurrency(row.eacCost)}</SensitiveData>
                          </TableCell>
                          <TableCell className="text-right text-xs tabular-nums">
                            <SensitiveData>{formatCurrency(row.budget)}</SensitiveData>
                          </TableCell>
                          <TableCell className="text-right text-xs tabular-nums font-medium">
                            <span className={row.variance > 0 ? 'text-destructive' : 'text-success-600 dark:text-success-400'}>
                              {row.variance > 0 ? '+' : ''}{formatCurrency(row.variance)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={healthToVariant(row.health)}
                              className="gap-1 text-xs"
                            >
                              <StatusIndicator
                                variant={row.health === 'green' ? 'success' : row.health === 'amber' ? 'warning' : 'error'}
                                size="sm"
                              />
                              {row.health === 'green' ? 'Healthy' : row.health === 'amber' ? 'At Risk' : 'Critical'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* ── Footer — navigate to project list ────────────────────── */}
              <div className="flex justify-end pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground"
                  onClick={() => navigateHook('/projects')}
                >
                  View all projects
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      }

      // ── PM cross-persona widgets (added by Exec via settings cog) ───────────
      // When an Executive with pmDashboard permission adds a PM widget, these
      // cases delegate to the ProjectManagerDashboardShell by embedding it as
      // a restricted single-widget renderer.  Each case renders a labelled
      // wrapper so the user knows this is a PM-perspective widget.
      case 'pm_pulse':
      case 'my_projects_health':
      case 'budget_hours_burn':
      case 'schedule_health':
      case 'my_team_this_week':
      case 'upcoming_billing_events':
      case 'timesheet_status':
      case 'quick_links_pm': {
        return (
          <PmWidgetBridge
            key={widgetId}
            widgetId={widgetId}
            onNavigate={handleNavigate}
          />
        );
      }

      default:
        return null;
    }
  }



  // ── Render ordered widgets ────────────────────────────────────────────────────
  // Start from the user's ordered/visible widget list, then apply
  // integration-gating rules: widgets that require an integration should only
  // appear once that integration is connected — no empty shells or connect
  // prompts on the dashboard itself.
  let widgetIds = orderedVisibleWidgets.filter(
    id => !isWidgetHiddenForReleaseAnyPersona(id),
  );

  // Utilisation Snapshot — requires planner OR timesheet integration.
  // If neither is connected, remove it entirely.
  if (!hasAnyUtilisationIntegration) {
    widgetIds = widgetIds.filter(id => id !== 'utilisation_snapshot');
  }

  // Check if any PM cross-persona widgets are in the visible list
  const pmWidgetIds = WIDGET_REGISTRY['project_manager'].map(w => w.id);
  const hasPmWidgets = widgetIds.some(id => pmWidgetIds.includes(id));

  const content = (
    <div className="space-y-6">
      {widgetIds.map(id =>
        isWidgetReady(id) ? renderWidget(id) : <WidgetSkeleton key={id} widgetId={id} />
      )}
    </div>
  );

  // Wrap with PM data provider only when PM widgets are present
  if (hasPmWidgets) {
    return <PMCrossPersonaDataProvider>{content}</PMCrossPersonaDataProvider>;
  }

  return content;
}
